/**
 * Ring Configurator V3
 * 3-step flow: Diamond → Setting → Review
 * Uses theme's native gallery + variant picker via Section Rendering API.
 */

class RingConfiguratorV3 extends HTMLElement {
  constructor() {
    super();

    // Read initial step from data attribute (set by Liquid based on current collection)
    const initialStep = parseInt(this.dataset.initialStep, 10) || 1;

    this.state = {
      currentStep: initialStep,
      selectedDiamond: null,
      // { id, variantId, handle, title, price, compareAtPrice, image, spec }
      selectedDiamondCollectionHandle: '',
      selectedSetting: null,
      // { id, handle, title, price, image, variants, options }
      selectedVariant: null,
      // The specific setting variant (metal + stone shape + ring size)
      selectedMetal: null,
      selectedStoneShape: null,
      selectedSettingCaratBucket: null,
      selectedRingSize: null,
      engravingEnabled: false,
      engravingText: '',
      engravingFont: 'classic_serif',
    };

    this.engravingFonts = [
      {
        id: 'classic_serif',
        label: 'Classic Serif',
        fontFamily: 'Georgia, "Times New Roman", serif',
      },
      {
        id: 'modern_sans',
        label: 'Modern Sans',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
      },
      {
        id: 'signature_script',
        label: 'Signature Script',
        fontFamily: '"Snell Roundhand", "Brush Script MT", "Segoe Script", cursive',
      },
      {
        id: 'refined_serif',
        label: 'Refined Serif',
        fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
      }
    ];
    this.engravingDraft = {
      text: '',
      font: 'classic_serif',
    };

    this.defaultDiamondCollectionHandle = this.dataset.diamondCollection || '';
    this.diamondCollectionHandle = this.defaultDiamondCollectionHandle;
    this.diamondCollectionUrl = this.dataset.diamondCollectionUrl || '';
    this.hasDiamondShapeCollections = this.dataset.hasDiamondShapeCollections === 'true';
    this.settingsCollectionHandle = this.dataset.settingsCollection;
    this.settingsSource = this.dataset.settingsSource || 'collection';
    this.usesManualSettingsProducts = this.settingsSource === 'manual';
    this.manualSettingsCount = parseInt(this.dataset.manualSettingsCount, 10) || 0;
    this.sectionId = this.dataset.sectionId;
    this.renderSectionId = this.dataset.renderSectionId || this.sectionId;
    this.sectionPreviewId = 'rc-product-preview';
    this.storageKey = this.dataset.storageKey || 'ringConfiguratorV3';
    this.checkoutMode = this.dataset.checkoutMode === 'direct_checkout' ? 'direct_checkout' : 'add_to_cart';
    this.isCollectionPage = this.dataset.isCollectionPage === 'true';
    this.engravingOptionEnabled = this.dataset.engravingEnabled === 'true';
    this.engravingProductHandle = this.dataset.engravingProductHandle || '';
    this.engravingVariantId = parseInt(this.dataset.engravingVariantId, 10) || null;
    this.engravingMaxChars = Math.max(1, parseInt(this.dataset.engravingMaxChars, 10) || 5);
    this.shapeMatchingEnabled = this.dataset.shapeMatchingEnabled !== 'false';
    this.settingsShapeFilterParams = this.parseShapeFilterParams(this.dataset.settingsShapeFilterParams);
    this.shapeFilterDisabled = false;
    this.lastAppliedSettingsShapeKey = '';
    this.lastSettingsRequestUrl = '';
    this.engravingProduct = null;
    this._engravingFetchPromise = null;
    this.diamondCollectionHydrated = false;
    this.activeFilterCollectionHandle = null;
    this.previewProductHandle = '';
    this.previewVariantId = null;
    this._previewVariantReloadTimer = null;
    this._pendingPreviewVariantReload = null;
    this._variantResolutionToken = 0;
    this._variantResolutionAbortController = null;
    this._continueTriggerLockUntil = 0;
    this._mobileQuickViewSelectLockUntil = 0;
    this._mobileQuickViewSelectInFlight = false;
    this._quickViewClosingGuardTimer = null;
    this._quickViewOpenTimer = null;
    this._pendingQuickViewHandle = '';
    this._pendingQuickViewStep = 0;
    this._boundQuickViewAfterShow = null;
    this._boundQuickViewAfterHide = null;
    this._boundEngravingModalAfterHide = null;
    this._boundDiamondShapeScrollUpdate = null;
    this._diamondShapeScrollRaf = null;
    this._diamondFiltersMarkup = '';
    this._diamondCountMarkup = '';
    this._settingsFiltersMarkup = '';
    this._settingsCountMarkup = '';
    this._manualSettingsGridMarkup = '';
    this._productJsonCache = new Map();
    this._sectionRenderCache = new Map();
    this.isThemeEditor = typeof Shopify !== 'undefined' && !!Shopify.designMode;
  }

  connectedCallback() {
    if (document.body) {
      document.body.classList.add('rc2-hide-mobile-dock');
    }

    this.cacheDOM();
    this.captureManualSettingsProductsGrid();
    this.captureCurrentDiamondFilterUi();
    this.bindEvents();
    this.loadState();
    this.dataset.currentStep = this.state.currentStep;
    this.updateButtons();
    this.updateStepImages();
    this.forceHeaderVisibility();
    this.setupBidirectionalPreview();
    this.setupInfinitePaginationObserver();

    this.initializeStepCollections();

    if (this.isThemeEditor) {
      window.requestAnimationFrame(() => {
        if (this.shouldRestoreDesignModeEngravingModal()) {
          this.openEngravingModal();
        }
      });
    }
  }

  captureCurrentDiamondFilterUi() {
    const filtersWrap = this.querySelector('#RC2FiltersWrap');
    const productCount = this.querySelector('#ProductCount');
    const diamondGrid = this.querySelector('#RC2Grid');
    const renderedHandle = (diamondGrid?.dataset?.collectionHandle || '').trim();
    const isDiamondContext = !this.diamondCollectionHandle || !renderedHandle || renderedHandle === this.diamondCollectionHandle;

    if (filtersWrap && isDiamondContext) {
      this.removeHiddenFilterGroups(filtersWrap);
      this._diamondFiltersMarkup = filtersWrap.innerHTML;
    }
    if (productCount && isDiamondContext) {
      this._diamondCountMarkup = productCount.innerHTML;
    }
    if (diamondGrid?.dataset.collectionHandle && isDiamondContext) {
      this.activeFilterCollectionHandle = diamondGrid.dataset.collectionHandle;
    }
  }

  captureManualSettingsProductsGrid() {
    if (!this.usesManualSettingsProducts) return;
    const settingsGrid = this.querySelector('#RC2SettingsGrid[data-manual-settings="true"]');
    if (!settingsGrid) return;
    this._manualSettingsGridMarkup = settingsGrid.innerHTML;
    this.settingsLoaded = true;
  }

  disconnectedCallback() {
    if (this._variantObserver) {
      this._variantObserver.disconnect();
      this._variantObserver = null;
    }

    if (this._variantResolutionAbortController) {
      this._variantResolutionAbortController.abort();
      this._variantResolutionAbortController = null;
    }

    if (this._boundQuickViewLoaded) {
      document.removeEventListener('quickview:loaded', this._boundQuickViewLoaded);
      this._boundQuickViewLoaded = null;
    }

    if (this.mobileQuickView && this._boundQuickViewSelectClick) {
      this.mobileQuickView.removeEventListener('click', this._boundQuickViewSelectClick);
      this._boundQuickViewSelectClick = null;
    }
    if (this.mobileQuickView && this._boundQuickViewAfterShow) {
      this.mobileQuickView.removeEventListener('modal:afterShow', this._boundQuickViewAfterShow);
      this._boundQuickViewAfterShow = null;
    }
    if (this.mobileQuickView && this._boundQuickViewAfterHide) {
      this.mobileQuickView.removeEventListener('modal:afterHide', this._boundQuickViewAfterHide);
      this._boundQuickViewAfterHide = null;
    }
    if (this.engravingModal && this._boundEngravingModalAfterHide) {
      this.engravingModal.removeEventListener('modal:afterHide', this._boundEngravingModalAfterHide);
      this._boundEngravingModalAfterHide = null;
    }
    if (this.engravingModal?.parentElement === document.body) {
      this.engravingModal.remove();
      this.engravingModal = null;
    }

    if (this._infiniteLoadObserver) {
      this._infiniteLoadObserver.disconnect();
      this._infiniteLoadObserver = null;
    }

    if (this._quickViewMediaObserver) {
      this._quickViewMediaObserver.disconnect();
      this._quickViewMediaObserver = null;
    }
    if (this._previewMediaObserver) {
      this._previewMediaObserver.disconnect();
      this._previewMediaObserver = null;
    }
    if (this._previewVariantReloadTimer) {
      window.clearTimeout(this._previewVariantReloadTimer);
      this._previewVariantReloadTimer = null;
    }
    this._pendingPreviewVariantReload = null;
    if (this._quickViewClosingGuardTimer) {
      window.clearTimeout(this._quickViewClosingGuardTimer);
      this._quickViewClosingGuardTimer = null;
    }
    if (this._quickViewOpenTimer) {
      window.clearTimeout(this._quickViewOpenTimer);
      this._quickViewOpenTimer = null;
    }
    this._pendingQuickViewHandle = '';
    this._pendingQuickViewStep = 0;

    if (this._stepTransitionTimer) {
      window.clearTimeout(this._stepTransitionTimer);
      this._stepTransitionTimer = null;
    }
    if (this._panelSwitchTimer) {
      window.clearTimeout(this._panelSwitchTimer);
      this._panelSwitchTimer = null;
    }

    if (this._previewRevealTimer) {
      window.clearTimeout(this._previewRevealTimer);
      this._previewRevealTimer = null;
    }
    if (this._previewHeightUnlockTimer) {
      window.clearTimeout(this._previewHeightUnlockTimer);
      this._previewHeightUnlockTimer = null;
    }
    if (this._previewHeightUnlockFrame) {
      window.cancelAnimationFrame(this._previewHeightUnlockFrame);
      this._previewHeightUnlockFrame = null;
    }

    if (this.layout) {
      this.layout.classList.remove('is-step-transitioning');
    }
    if (this.content) {
      this.content.classList.remove('is-panel-switching');
      this.content.style.removeProperty('--rc2-panel-switch-height');
    }
    if (this.stepsEl) {
      this.stepsEl.forEach((step) => {
        step.classList.remove('is-activating');
        if (step._rc2ActivateTimer) {
          window.clearTimeout(step._rc2ActivateTimer);
          step._rc2ActivateTimer = null;
        }
      });
    }

    if (this.preview) {
      this.preview.classList.remove('is-loading', 'is-ready');
    }

    if (document.body && !document.querySelector('ring-configurator-v3')) {
      document.body.classList.remove('rc2-hide-mobile-dock');
    }
    this.classList.remove('is-quick-view-open');
  }

  async initializeStepCollections() {
    if (this.state.currentStep === 1 && this.needsDiamondCollectionRefresh()) {
      await this.loadDiamondCollection();
    }

    if (this.state.currentStep === 2) {
      if (this.usesManualSettingsProducts) {
        this.hydrateManualSettingsProducts();
      } else {
        await this.loadSettingsCollection();
      }
    }

    if (this.state.currentStep === 1) {
      if (this.state.selectedDiamond) {
        this.restoreCardSelection('#RC2Grid', this.state.selectedDiamond.handle);
      }
    }
  }

  /* ===================== FORCE HEADER VISIBILITY ===================== */
  forceHeaderVisibility() {
    // The theme's .section::before pseudo-element creates a white overlay
    // with position:absolute that covers all non-positioned content.
    // Cancel any WAAPI animations the theme may apply to our headers,
    // and ensure they stay visible above the overlay.
    this.querySelectorAll('.rc2-content__header, .rc2-content__header h2, .rc2-content__header p').forEach(el => {
      // Cancel WAAPI animations (Motion One / theme scroll reveals)
      if (el.getAnimations) {
        el.getAnimations().forEach(a => a.cancel());
      }
    });
  }

  /* ===================== BIDIRECTIONAL STICKY PREVIEW ===================== */
  /**
   * When the gallery panel is taller than the viewport:
   *   scroll DOWN → gallery follows down (bottom stays visible)
   *   scroll UP   → gallery follows up (top stays visible)
   * When the gallery fits in the viewport, CSS sticky handles it normally.
   */
  setupBidirectionalPreview() {
    const preview = this.preview;
    if (!preview) return;

    const BOTTOM_PAD = 24;  // breathing room from viewport bottom
    const getTopOffset = () => {
      const rc2Top = parseFloat(getComputedStyle(this).getPropertyValue('--rc2-sticky-top'));
      if (!Number.isNaN(rc2Top) && rc2Top > 0) return rc2Top;

      const themeStickyTop = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')
      );
      if (!Number.isNaN(themeStickyTop) && themeStickyTop > 0) return themeStickyTop;

      return 0;
    };

    let lastScrollY = window.scrollY;

    const tick = () => {
      // Skip on mobile where preview is hidden / stacked
      if (window.innerWidth <= 1024) {
        preview.style.top = '';
        lastScrollY = window.scrollY;
        return;
      }

      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      lastScrollY = scrollY;

      if (delta === 0) return;

      const TOP_OFFSET = getTopOffset();
      const viewportH = window.innerHeight;
      const previewH = preview.offsetHeight;

      // If preview fits in the viewport let CSS sticky do its job
      if (previewH <= viewportH - TOP_OFFSET - BOTTOM_PAD) {
        preview.style.top = '';
        return;
      }

      // Clamp between:
      //   maxTop = TOP_OFFSET           → gallery pinned at top
      //   minTop = viewportH - previewH - BOTTOM_PAD  → gallery pinned at bottom
      const maxTop = TOP_OFFSET;
      const minTop = viewportH - previewH - BOTTOM_PAD;
      const curTop = parseFloat(preview.style.top) || TOP_OFFSET;
      const newTop = Math.min(maxTop, Math.max(minTop, curTop - delta));

      preview.style.top = newTop + 'px';
    };

    window.addEventListener('scroll', tick, { passive: true });

    // Reset on resize so a newly sized gallery recalculates cleanly
    window.addEventListener('resize', () => {
      preview.style.top = '';
      lastScrollY = window.scrollY;
    }, { passive: true });

    this._previewScrollHandler = tick;
  }

  /* ===================== DOM CACHE ===================== */
  cacheDOM() {
    this.stepsEl = this.querySelectorAll('.rc2-step');
    this.lines = this.querySelectorAll('.rc2-step__line');
    this.panels = this.querySelectorAll('.rc2-step-panel');
    this.layout = this.querySelector('.rc2-layout');
    this.preview = this.querySelector('#RC2Preview');
    this.content = this.querySelector('#RC2Content');
    this.summary = this.querySelector('#RC2Summary');
    this.continueBtn = this.querySelector('[data-continue]');
    this.backBtn = this.querySelector('[data-back]');
    this.diamondShapesWrap = this.querySelector('[data-diamond-shapes-wrap]');
    this.diamondShapesScroller = this.querySelector('[data-diamond-shapes-scroller]');
    this.diamondShapesNextBtn = this.querySelector('[data-diamond-shapes-next]');
    this.diamondShapeButtons = Array.from(this.querySelectorAll('[data-diamond-shape-collection]'));

    // Summary data displays
    this.sumDiamond = this.querySelector('[data-sum-diamond]');
    this.sumSetting = this.querySelector('[data-sum-setting]');
    this.sumMetal = this.querySelector('[data-sum-metal]');
    this.sumSize = this.querySelector('[data-sum-size]');
    this.sumTotal = this.querySelector('[data-sum-total]');
    this.sumEngraving = this.querySelector('[data-sum-engraving]');
    this.sumEngravingRow = this.querySelector('[data-sum-engraving-row]');

    this.mobileQuickView = this.querySelector(`#RC2MobileQuickView-${this.sectionId}`) || document.getElementById(`RC2MobileQuickView-${this.sectionId}`);
    this.mobileQuickViewSelectBtn = this.mobileQuickView?.querySelector('[data-rc2-mobile-select]');

    this.mobileStrip = this.querySelector('.rc2-mobile-strip');
    this.mobileDiamondCard = this.querySelector('[data-mobile-diamond-card]');
    this.mobileSettingCard = this.querySelector('[data-mobile-setting-card]');
    this.mobileDiamondImg = this.querySelector('[data-mobile-diamond-img]');
    this.mobileSettingImg = this.querySelector('[data-mobile-setting-img]');
    this.mobileDiamondLabel = this.querySelector('[data-mobile-diamond-label]');
    this.mobileSettingLabel = this.querySelector('[data-mobile-setting-label]');

    this.engravingModal = this.querySelector(`#RC2EngravingModal-${this.sectionId}`) || document.getElementById(`RC2EngravingModal-${this.sectionId}`);
    this.engravingModalContent = this.engravingModal?.querySelector('.drawer__content');
    this.engravingModalScrollable = this.engravingModal?.querySelector('.drawer__scrollable');
    this.engravingModalInput = this.engravingModal?.querySelector('[data-engraving-modal-input]');
    this.engravingModalCount = this.engravingModal?.querySelector('[data-engraving-modal-count]');
    this.engravingModalPreviewOverlay = this.engravingModal?.querySelector('[data-engraving-preview-overlay]');
    this.engravingModalPreviewText = this.engravingModal?.querySelector('[data-engraving-preview-text]');
    this.engravingModalPreviewCurve = this.engravingModal?.querySelector('[data-engraving-preview-curve]');
    this.engravingModalPreviewCurveText = this.engravingModal?.querySelector('[data-engraving-preview-curve-text]');
    this.engravingModalError = this.engravingModal?.querySelector('[data-engraving-modal-error]');
    this.engravingModalApplyBtn = this.engravingModal?.querySelector('[data-engraving-modal-apply]');
    this.engravingModalFontButtons = this.engravingModal
      ? Array.from(this.engravingModal.querySelectorAll('[data-engraving-font-option]'))
      : [];
  }

  findDiamondShapeButton(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;
    return (this.diamondShapeButtons || []).find((button) => button.dataset.collectionHandle === normalizedHandle) || null;
  }

  getDiamondCollectionUrl(handle = this.diamondCollectionHandle) {
    const button = this.findDiamondShapeButton(handle);
    return button?.dataset.collectionUrl || this.diamondCollectionUrl || `${window.Shopify?.routes?.root || '/'}collections/${handle}`;
  }

  updateDiamondShapeButtons(activeHandle = this.diamondCollectionHandle) {
    const normalizedHandle = String(activeHandle || '').trim();
    (this.diamondShapeButtons || []).forEach((button) => {
      const isActive = button.dataset.collectionHandle === normalizedHandle;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  setupDiamondShapeScrollControls() {
    if (!this.diamondShapesScroller || !this.diamondShapesNextBtn) return;

    if (!this._boundDiamondShapeScrollUpdate) {
      this._boundDiamondShapeScrollUpdate = () => this.scheduleDiamondShapeScrollUpdate();
    }

    this.diamondShapesScroller.addEventListener('scroll', this._boundDiamondShapeScrollUpdate, { passive: true });
    window.addEventListener('resize', this._boundDiamondShapeScrollUpdate, { passive: true });

    this.diamondShapesNextBtn.addEventListener('click', () => {
      this.scrollDiamondShapesForward();
    });

    window.requestAnimationFrame(() => this.updateDiamondShapeScrollControls());
  }

  scheduleDiamondShapeScrollUpdate() {
    if (this._diamondShapeScrollRaf) return;

    this._diamondShapeScrollRaf = window.requestAnimationFrame(() => {
      this._diamondShapeScrollRaf = null;
      this.updateDiamondShapeScrollControls();
    });
  }

  updateDiamondShapeScrollControls() {
    const scroller = this.diamondShapesScroller;
    const wrap = this.diamondShapesWrap;
    const nextButton = this.diamondShapesNextBtn;
    if (!scroller || !wrap || !nextButton) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const isScrollLocked = maxScroll <= 2;
    const isAtStart = scroller.scrollLeft <= 2;
    const isAtEnd = isScrollLocked || scroller.scrollLeft >= maxScroll - 2;

    wrap.classList.toggle('is-scroll-locked', isScrollLocked);
    wrap.classList.toggle('is-at-start', isAtStart);
    wrap.classList.toggle('is-at-end', isAtEnd);
    nextButton.classList.toggle('is-hidden', isAtEnd);
    nextButton.setAttribute('aria-hidden', isAtEnd ? 'true' : 'false');
    nextButton.tabIndex = isAtEnd ? -1 : 0;
  }

  scrollDiamondShapesForward() {
    const scroller = this.diamondShapesScroller;
    if (!scroller) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = Math.max(220, scroller.clientWidth * 0.72);
    scroller.scrollBy({
      left: distance,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });

    window.setTimeout(() => this.updateDiamondShapeScrollControls(), reducedMotion ? 0 : 260);
  }

  setActiveDiamondCollection(handle, url = '') {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return false;

    this.diamondCollectionHandle = normalizedHandle;
    this.diamondCollectionUrl = String(url || this.getDiamondCollectionUrl(normalizedHandle) || '').trim();
    this.dataset.diamondCollection = normalizedHandle;
    if (this.diamondCollectionUrl) {
      this.dataset.diamondCollectionUrl = this.diamondCollectionUrl;
    }
    this.updateDiamondShapeButtons(normalizedHandle);
    this.scheduleDiamondShapeScrollUpdate();
    return true;
  }

  /* ===================== EVENT BINDING ===================== */
  bindEvents() {
    // Step indicator clicks
    this.stepsEl.forEach(step => {
      const activateStep = () => {
        const n = parseInt(step.dataset.step);
        if (this.canGoToStep(n)) this.goToStep(n);
      };

      step.addEventListener('click', activateStep);
      step.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activateStep();
      });
    });

    // Continue button
    if (this.continueBtn) {
      const triggerContinue = (event) => {
        event?.preventDefault?.();
        const now = Date.now();
        if (now < this._continueTriggerLockUntil) return;
        this._continueTriggerLockUntil = now + 320;
        this.handleContinue();
      };
      this.continueBtn.addEventListener('pointerup', triggerContinue);
      this.continueBtn.addEventListener('click', triggerContinue);
    }

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.handleBack());
    }

    this.addEventListener('click', (e) => {
      const resetBtn = e.target.closest('[data-reset]');
      if (!resetBtn) return;
      e.preventDefault();
      this.resetToStepOne();
    });

    this.addEventListener('click', (e) => {
      const shapeButton = e.target.closest('[data-diamond-shape-collection]');
      if (!shapeButton) return;
      e.preventDefault();
      this.handleDiamondShapeCollectionSelect(shapeButton);
    });

    this.setupDiamondShapeScrollControls();

    this.addEventListener('click', (e) => {
      const showAllBtn = e.target.closest('[data-rc2-show-all-settings]');
      if (!showAllBtn) return;
      e.preventDefault();
      this.handleShowAllSettings();
    });

    this.addEventListener('click', (e) => {
      const changeDiamondBtn = e.target.closest('[data-rc2-change-diamond]');
      if (!changeDiamondBtn) return;
      e.preventDefault();
      this.goToStep(1);
    });

    this.addEventListener('click', (e) => {
      const engravingOpenBtn = e.target.closest('[data-engraving-open]');
      if (!engravingOpenBtn) return;
      e.preventDefault();
      this.openEngravingModal();
    });

    this.addEventListener('click', (e) => {
      const engravingRemoveBtn = e.target.closest('[data-engraving-remove]');
      if (!engravingRemoveBtn) return;
      e.preventDefault();
      this.state.engravingEnabled = false;
      this.updateSummary();
      this.updateButtons();
      this.saveState();
      this.renderReview();
    });

    this.addEventListener('click', (e) => {
      const stepTarget = e.target.closest('[data-mobile-step]');
      if (stepTarget) {
        e.preventDefault();
        const step = parseInt(stepTarget.dataset.mobileStep, 10);
        if (Number.isFinite(step) && this.canGoToStep(step)) {
          this.goToStep(step);
        }
        return;
      }

    });

    if (this.engravingModalInput) {
      this.engravingModalInput.addEventListener('input', () => {
        const normalized = this.normalizeEngravingText(this.engravingModalInput.value);
        if (this.engravingModalInput.value !== normalized) {
          this.engravingModalInput.value = normalized;
        }
        this.engravingDraft.text = normalized;
        this.updateEngravingModalUi();
      });
    }

    this.engravingModalFontButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.engravingDraft.font = this.normalizeEngravingFont(button.dataset.engravingFontOption);
        this.updateEngravingModalUi();
      });
    });

    if (this.engravingModal) {
      const cancelBtn = this.engravingModal.querySelector('[data-engraving-modal-cancel]');
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          this.setEngravingModalError('');
          this.engravingModal.hide();
        });
      }

      if (this.engravingModalApplyBtn) {
        this.engravingModalApplyBtn.addEventListener('click', () => {
          this.applyEngravingDraft();
        });
      }

      if (!this._boundEngravingModalAfterHide) {
        this._boundEngravingModalAfterHide = () => {
          this.setEngravingModalError('');
          this.setDesignModeEngravingModalState(false);
          this.unlockBodyScrollIfNoModals();
        };
        this.engravingModal.addEventListener('modal:afterHide', this._boundEngravingModalAfterHide);
      }
    }

    // Initialize sort dropdown (theme's FacetSort from collection.js
    // isn't loaded on this page, so we handle it ourselves)
    this.initSortDropdown();

    // Quick-view trigger (eye icon)
    this.addEventListener('click', (e) => {
      const quickViewBtn = e.target.closest('[data-rc2-quick-view]');
      if (!quickViewBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const card = quickViewBtn.closest('.rc2-card');
      if (!card) return;

      this.openMobileQuickView(card, quickViewBtn);
    });

    // Product card clicks (delegation)
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-rc2-quick-view]')) return;
      const card = e.target.closest('.rc2-card');
      if (!card) return;
      this.handleCardClick(card);
    });

    // Load More button (delegation)
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      this.handleLoadMore(btn);
    });

    // Regular pagination links (delegation)
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.rc2-pagination a.previous, .rc2-pagination a.next, .rc2-pagination a.page');
      if (!link) return;
      e.preventDefault();
      this.handlePaginationLink(link);
    });

    // The configurator owns both Step 1 and Step 2 grids, so we intercept
    // facet updates and render the active step grid manually.
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('form[is="facet-form"]');
      if (!form || (this.state.currentStep !== 1 && this.state.currentStep !== 2)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.state.currentStep === 1) {
        this.handleStep1FacetSubmit(form);
      } else {
        this.handleStep2FacetSubmit(form);
      }
    }, true);

    this.addEventListener('click', (e) => {
      const removeLink = e.target.closest('a[is="facet-remove"]');
      if (!removeLink || (this.state.currentStep !== 1 && this.state.currentStep !== 2)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.state.currentStep === 1) {
        this.handleStep1FacetRemove(removeLink);
      } else {
        this.handleStep2FacetRemove(removeLink);
      }
    }, true);

    if (this.mobileQuickViewSelectBtn) {
      const triggerQuickViewSelect = (event) => {
        event?.preventDefault?.();
        const now = Date.now();
        if (now < this._mobileQuickViewSelectLockUntil) return;
        this._mobileQuickViewSelectLockUntil = now + 320;
        this.handleMobileQuickViewSelect();
      };
      this.mobileQuickViewSelectBtn.addEventListener('pointerup', triggerQuickViewSelect);
      this.mobileQuickViewSelectBtn.addEventListener('click', triggerQuickViewSelect);
    }

    if (this.mobileQuickView && !this._boundQuickViewLoaded) {
      this._boundQuickViewLoaded = (event) => {
        const activeProductUrl = (this.mobileQuickView.getAttribute('data-product-url') || '').split('?')[0];
        const loadedProductUrl = (event?.detail?.productUrl || '').split('?')[0];
        if (!activeProductUrl || !loadedProductUrl || activeProductUrl !== loadedProductUrl) return;

        const quickViewContent = this.mobileQuickView.querySelector('.quick-view__content');
        if (!quickViewContent) return;

        this.ensureDesktopQuickViewAction(quickViewContent);
        this.setupQuickViewMediaBehavior(quickViewContent);
      };
      document.addEventListener('quickview:loaded', this._boundQuickViewLoaded);
    }

    if (this.mobileQuickView && !this._boundQuickViewSelectClick) {
      this._boundQuickViewSelectClick = (event) => {
        const desktopSelectBtn = event.target.closest('[data-rc2-desktop-select]');
        if (!desktopSelectBtn) return;
        event.preventDefault();
        this.handleMobileQuickViewSelect();
      };
      this.mobileQuickView.addEventListener('click', this._boundQuickViewSelectClick);
    }

    if (this.mobileQuickView && !this._boundQuickViewAfterShow) {
      this._boundQuickViewAfterShow = () => {
        this.setQuickViewOpenState(true);
      };
      this.mobileQuickView.addEventListener('modal:afterShow', this._boundQuickViewAfterShow);
    }

    if (this.mobileQuickView && !this._boundQuickViewAfterHide) {
      this._boundQuickViewAfterHide = () => {
        this.setQuickViewOpenState(false);
        this.mobileQuickView.dataset.rc2SelectedHandle = '';
        this.mobileQuickView.dataset.rc2SelectedStep = '';
        this.updateQuickViewSelectButtons();
      };
      this.mobileQuickView.addEventListener('modal:afterHide', this._boundQuickViewAfterHide);
    }
  }

  /* ===================== NAVIGATION ===================== */
  handleDiamondShapeCollectionSelect(button) {
    if (!button) return;
    const nextHandle = String(button.dataset.collectionHandle || '').trim();
    if (!nextHandle) return;

    const nextUrl = button.dataset.collectionUrl || this.getDiamondCollectionUrl(nextHandle);
    const previousHandle = this.diamondCollectionHandle;
    const changed = nextHandle !== previousHandle;

    this.setActiveDiamondCollection(nextHandle, nextUrl);
    if (!changed && !this.needsDiamondCollectionRefresh()) return;

    this.diamondCollectionHydrated = false;
    this.activeFilterCollectionHandle = nextHandle;
    this._diamondFiltersMarkup = '';
    this._diamondCountMarkup = '';

    this.state.currentStep = 1;
    this.state.selectedDiamond = null;
    this.state.selectedDiamondCollectionHandle = nextHandle;
    this.clearSettingSelection();
    this.shapeFilterDisabled = false;
    this.settingsLoaded = false;
    this.lastAppliedSettingsShapeKey = '';
    this._settingsFiltersMarkup = '';
    this._settingsCountMarkup = '';

    this.resetCardSelections();
    this.clearPreview();
    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();
    this.saveState();

    this.loadDiamondCollection(nextUrl);
  }

  canGoToStep(n) {
    if (n === 1) return true;
    if (n === 2) return this.state.selectedDiamond !== null;
    if (n === 3) return this.state.selectedDiamond !== null && this.state.selectedSetting !== null && this.isSelectedSettingCompatibleWithDiamond();
    return false;
  }

  needsDiamondCollectionRefresh() {
    if (!this.diamondCollectionHandle) return false;
    const grid = this.querySelector('#RC2Grid');
    if (!grid) return false;

    const renderedHandle = (grid.dataset.collectionHandle || '').trim();
    const handleMismatch = renderedHandle !== '' && renderedHandle !== this.diamondCollectionHandle;
    const standaloneNeedsHydration = !this.isCollectionPage && !this.diamondCollectionHydrated;
    const filtersOnDifferentCollection = this.activeFilterCollectionHandle
      && this.activeFilterCollectionHandle !== this.diamondCollectionHandle;

    return handleMismatch || standaloneNeedsHydration || filtersOnDifferentCollection;
  }

  needsSettingsCollectionRefresh() {
    if (this.usesManualSettingsProducts) {
      const grid = this.querySelector('#RC2SettingsGrid');
      if (!grid) return true;
      const renderedShapeKey = (grid.dataset.shapeMatchKey || '').trim();
      const expectedShapeKey = this.getActiveSettingsShapeKey();
      return !this.settingsLoaded || (this.shapeMatchingEnabled && renderedShapeKey !== expectedShapeKey);
    }

    if (!this.settingsCollectionHandle) return false;
    const grid = this.querySelector('#RC2SettingsGrid');
    if (!grid) return true;

    const renderedHandle = (grid.dataset.collectionHandle || '').trim();
    const renderedShapeKey = (grid.dataset.shapeMatchKey || '').trim();
    const handleMismatch = renderedHandle !== '' && renderedHandle !== this.settingsCollectionHandle;
    const neverLoaded = !this.settingsLoaded;
    const expectedShapeKey = this.getActiveSettingsShapeKey();
    const shapeMismatch = this.shapeMatchingEnabled && renderedShapeKey !== expectedShapeKey;

    return neverLoaded || handleMismatch || shapeMismatch;
  }

  applyCachedFilterUi(markup = '', countMarkup = '') {
    const filtersWrap = this.querySelector('#RC2FiltersWrap');
    if (filtersWrap && typeof markup === 'string' && markup !== '') {
      filtersWrap.innerHTML = markup;
      this.removeHiddenFilterGroups(filtersWrap);
    }

    const productCount = this.querySelector('#ProductCount');
    if (productCount && typeof countMarkup === 'string' && countMarkup !== '') {
      productCount.innerHTML = countMarkup;
    }
  }

  applyCachedDiamondFilterUi() {
    this.applyCachedFilterUi(this._diamondFiltersMarkup, this._diamondCountMarkup);
  }

  applyCachedSettingsFilterUi() {
    if (this.usesManualSettingsProducts) {
      this.clearSettingsFilterUiForManualProducts();
      return;
    }
    this.applyCachedFilterUi(this._settingsFiltersMarkup, this._settingsCountMarkup);
  }

  removeHiddenFilterGroups(scope = this) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    const hiddenLabels = new Set(['diamond shape']);
    scope.querySelectorAll('[data-filter]').forEach((filterGroup) => {
      const labelElement = filterGroup.querySelector('.details__summary .text-base-xl, .details__summary label');
      const label = String(labelElement?.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      if (hiddenLabels.has(label)) {
        filterGroup.remove();
      }
    });
  }

  clearSettingsFilterUiForManualProducts() {
    const filtersWrap = this.querySelector('#RC2FiltersWrap');
    if (filtersWrap) {
      filtersWrap.innerHTML = '';
    }
  }

  triggerStepTransition() {
    const layout = this.layout;
    if (!layout) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    layout.classList.remove('is-step-transitioning');

    if (reducedMotion) return;

    // Force reflow so rapid step changes still replay the animation.
    void layout.offsetWidth;
    layout.classList.add('is-step-transitioning');

    if (this._stepTransitionTimer) {
      window.clearTimeout(this._stepTransitionTimer);
    }
    this._stepTransitionTimer = window.setTimeout(() => {
      layout.classList.remove('is-step-transitioning');
    }, 320);
  }

  resetPreviewPosition() {
    if (!this.preview) return;
    this.preview.style.top = '';
  }

  getPreviewFallbackHeight() {
    if (!this.preview) return 760;
    const previewWidth = this.preview.clientWidth || this.getBoundingClientRect().width * 0.38;
    const mediaHeight = previewWidth > 0 ? (previewWidth * 0.75) : 340; // 4:3 visual stage
    const detailsHeight = this.state.currentStep === 1 ? 420 : 460;
    const estimated = Math.round(mediaHeight + detailsHeight);
    return Math.min(980, Math.max(760, estimated));
  }

  lockPreviewHeightForLoading() {
    if (!this.preview || this.isMobileViewport()) return;

    const currentHeight = Math.round(this.preview.getBoundingClientRect().height);
    const fallbackHeight = this.getPreviewFallbackHeight();
    const safeCurrentHeight = currentHeight > 0 ? Math.min(currentHeight, 980) : 0;
    const lockHeight = Math.max(safeCurrentHeight, fallbackHeight);

    this.preview.style.minHeight = `${lockHeight}px`;
    this.preview.style.height = `${lockHeight}px`;
    this.preview.style.setProperty('--rc2-preview-lock-height', `${lockHeight}px`);
    this.preview.classList.add('is-height-locked');

    if (this._previewHeightUnlockTimer) {
      window.clearTimeout(this._previewHeightUnlockTimer);
      this._previewHeightUnlockTimer = null;
    }
    if (this._previewHeightUnlockFrame) {
      window.cancelAnimationFrame(this._previewHeightUnlockFrame);
      this._previewHeightUnlockFrame = null;
    }
  }

  releasePreviewHeightLock() {
    if (!this.preview || this.isMobileViewport()) return;

    const clearLock = () => {
      if (!this.preview) return;
      this.preview.style.minHeight = '';
      this.preview.style.height = '';
      this.preview.style.removeProperty('--rc2-preview-lock-height');
      this.preview.classList.remove('is-height-locked');
      if (this._previewHeightUnlockTimer) {
        window.clearTimeout(this._previewHeightUnlockTimer);
        this._previewHeightUnlockTimer = null;
      }
      if (this._previewHeightUnlockFrame) {
        window.cancelAnimationFrame(this._previewHeightUnlockFrame);
        this._previewHeightUnlockFrame = null;
      }
    };

    const releaseWhenLayoutStable = () => {
      if (!this.preview) {
        clearLock();
        return;
      }

      let checks = 0;
      let stableFrames = 0;
      let lastHeight = Math.round(this.preview.getBoundingClientRect().height);

      const tick = () => {
        if (!this.preview) {
          clearLock();
          return;
        }

        checks += 1;
        const currentHeight = Math.round(this.preview.getBoundingClientRect().height);
        if (Math.abs(currentHeight - lastHeight) <= 1) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }
        lastHeight = currentHeight;

        if (stableFrames >= 3 || checks >= 24) {
          clearLock();
          return;
        }

        this._previewHeightUnlockFrame = window.requestAnimationFrame(tick);
      };

      this._previewHeightUnlockFrame = window.requestAnimationFrame(tick);
    };

    const mediaAssets = Array.from(this.preview.querySelectorAll('.product__media img, .product__media video'));
    const pendingAssets = mediaAssets.filter((asset) => {
      if (asset instanceof HTMLImageElement) return !asset.complete;
      if (asset instanceof HTMLVideoElement) return asset.readyState < 2;
      return false;
    });

    if (pendingAssets.length > 0) {
      let remaining = pendingAssets.length;
      const onAssetReady = () => {
        remaining -= 1;
        if (remaining <= 0) {
          releaseWhenLayoutStable();
        }
      };

      pendingAssets.forEach((asset) => {
        asset.addEventListener('load', onAssetReady, { once: true });
        asset.addEventListener('error', onAssetReady, { once: true });
        if (asset instanceof HTMLVideoElement) {
          asset.addEventListener('loadeddata', onAssetReady, { once: true });
        }
      });

      this._previewHeightUnlockTimer = window.setTimeout(releaseWhenLayoutStable, 1800);
      return;
    }

    this._previewHeightUnlockTimer = window.setTimeout(releaseWhenLayoutStable, 120);
  }

  mountPreviewLoadingOverlay() {
    if (!this.preview) return;
    if (this.preview.querySelector('.rc2-preview-loading-overlay')) return;

    const overlay = document.createElement('div');
    overlay.className = 'rc2-preview-loading-overlay';
    overlay.innerHTML = `
      <span class="rc2-skeleton-shimmer rc2-preview-loading-overlay__scrim" aria-hidden="true"></span>
      <span class="rc2-preview-loading-overlay__label">Refreshing preview...</span>
    `;
    this.preview.appendChild(overlay);
  }

  removePreviewLoadingOverlay() {
    if (!this.preview) return;
    this.preview.querySelectorAll('.rc2-preview-loading-overlay').forEach((overlay) => overlay.remove());
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 1024px)').matches;
  }

  findCardByHandle(gridSelector, handle) {
    if (!gridSelector || !handle) return null;
    const grid = this.querySelector(gridSelector);
    if (!grid) return null;

    return Array.from(grid.querySelectorAll('.rc2-card')).find(
      (card) => card.dataset.productHandle === handle
    ) || null;
  }

  setQuickViewOpenState(isOpen) {
    this.classList.toggle('is-quick-view-open', !!isOpen);
  }

  setQuickViewClosingGuard(active, durationMs = 0) {
    this.classList.toggle('is-quick-view-closing', !!active);
    if (this._quickViewClosingGuardTimer) {
      window.clearTimeout(this._quickViewClosingGuardTimer);
      this._quickViewClosingGuardTimer = null;
    }
    if (!active) return;

    const delay = Math.max(0, parseInt(durationMs, 10) || 0);
    if (delay > 0) {
      this._quickViewClosingGuardTimer = window.setTimeout(() => {
        this.classList.remove('is-quick-view-closing');
        this._quickViewClosingGuardTimer = null;
      }, delay);
    }
  }

  openMobileQuickView(card, trigger = null) {
    if (!card) return;
    const handle = card.dataset.productHandle;
    if (!handle) return;
    this.openMobileQuickViewByHandle(handle, trigger || card);
  }

  openMobileQuickViewByHandle(handle, trigger = null) {
    if (!this.mobileQuickView || !handle) return;

    if (this.classList.contains('is-quick-view-closing')) {
      if (this._quickViewOpenTimer) {
        window.clearTimeout(this._quickViewOpenTimer);
      }
      this._pendingQuickViewHandle = handle;
      this._pendingQuickViewStep = this.state.currentStep;
      this._quickViewOpenTimer = window.setTimeout(() => {
        this._quickViewOpenTimer = null;
        const pending = this._pendingQuickViewHandle;
        const pendingStep = this._pendingQuickViewStep;
        this._pendingQuickViewHandle = '';
        this._pendingQuickViewStep = 0;
        if (pendingStep && pendingStep !== this.state.currentStep) return;
        if (pending) {
          this.openMobileQuickViewByHandle(pending, trigger);
        }
      }, 280);
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const productUrl = `${root}products/${handle}`;
    const modal = this.mobileQuickView;

    modal.setAttribute('data-product-url', productUrl);
    modal.dataset.rc2SelectedHandle = handle;
    modal.dataset.rc2SelectedStep = String(this.state.currentStep);
    this.updateQuickViewSelectButtons();
    this.setQuickViewOpenState(true);

    const isOpen = modal.open || modal.hasAttribute('open') || modal.hasAttribute('active');
    if (isOpen) {
      if (typeof modal.quickview === 'function') {
        modal.quickview();
      } else {
        this.closeQuickViewWithThemeAnimation().then(() => {
          this.openMobileQuickViewByHandle(handle, trigger);
        });
      }
      return;
    }

    modal.show(trigger || this);
  }

  getQuickViewSelectLabel() {
    const stepFromDrawer = parseInt(this.mobileQuickView?.dataset?.rc2SelectedStep, 10);
    const step = Number.isFinite(stepFromDrawer) && stepFromDrawer > 0
      ? stepFromDrawer
      : this.state.currentStep;
    return this.getStepActionLabel(step);
  }

  getStepActionLabel(step = this.state.currentStep) {
    if (step === 1) return 'Select this Diamond';
    if (step === 2) return 'Select this Setting';
    return this.getStepThreeActionLabel();
  }

  getStepThreeActionLabel() {
    return this.checkoutMode === 'direct_checkout' ? 'Order Your Ring' : 'Add to Cart';
  }

  updateQuickViewSelectButtons() {
    const label = this.getQuickViewSelectLabel();

    if (this.mobileQuickViewSelectBtn) {
      this.mobileQuickViewSelectBtn.textContent = label;
    }

    const desktopSelectBtn = this.mobileQuickView?.querySelector('[data-rc2-desktop-select]');
    if (desktopSelectBtn) {
      desktopSelectBtn.textContent = label;
    }
  }

  setQuickViewSelectButtonsDisabled(disabled) {
    if (this.mobileQuickViewSelectBtn) {
      this.mobileQuickViewSelectBtn.disabled = !!disabled;
    }
    const desktopSelectBtn = this.mobileQuickView?.querySelector('[data-rc2-desktop-select]');
    if (desktopSelectBtn) {
      desktopSelectBtn.disabled = !!disabled;
    }
  }

  ensureDesktopQuickViewAction(container) {
    if (!container) return;
    const productInfo = container.querySelector('.product__info');
    if (!productInfo) return;

    let actions = productInfo.querySelector('.rc2-desktop-qv-actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'rc2-desktop-qv-actions';
      actions.innerHTML = `
        <button type="button" class="rc2-desktop-qv-select" data-rc2-desktop-select>
          ${this.escapeHtml(this.getQuickViewSelectLabel())}
        </button>
      `;
      productInfo.appendChild(actions);
    }

    this.updateQuickViewSelectButtons();
  }

  setupQuickViewMediaBehavior(container) {
    if (!container) return;

    this.enforceVideoPlaybackBehavior(container);
    this.hideInternalCaratOptionFieldsets(container);
    this.applyQuickViewInternalSettingOptions(container);

    if (this._quickViewMediaObserver) {
      this._quickViewMediaObserver.disconnect();
      this._quickViewMediaObserver = null;
    }

    this._quickViewMediaObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;
        this.enforceVideoPlaybackBehavior(container);
        this.hideInternalCaratOptionFieldsets(container);
        this.applyQuickViewInternalSettingOptions(container);
        break;
      }
    });

    this._quickViewMediaObserver.observe(container, { childList: true, subtree: true });
  }

  setupPreviewMediaBehavior(container) {
    if (!container) return;

    this.enforceVideoPlaybackBehavior(container);

    if (this._previewMediaObserver) {
      this._previewMediaObserver.disconnect();
      this._previewMediaObserver = null;
    }

    this._previewMediaObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;
        this.enforceVideoPlaybackBehavior(container);
        break;
      }
    });

    this._previewMediaObserver.observe(container, { childList: true, subtree: true });
  }

  enforceVideoPlaybackBehavior(container) {
    if (!container) return;

    const videos = container.querySelectorAll('video');
    videos.forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;

      video.controls = false;
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;

      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback noplaybackrate');

      video.disablePictureInPicture = true;

      const playNow = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      };

      if (video.readyState >= 2) {
        playNow();
      } else {
        video.addEventListener('loadeddata', playNow, { once: true });
      }
    });
  }

  getQuickViewSelectedVariantId() {
    if (!this.mobileQuickView) return null;

    const inputs = this.mobileQuickView.querySelectorAll('.quick-view__content input[name="id"]');
    if (!inputs.length) return null;

    let fallbackId = null;
    for (const input of inputs) {
      const variantId = parseInt(input.value, 10);
      if (!Number.isFinite(variantId) || variantId <= 0) continue;
      if (!input.disabled) return variantId;
      if (!fallbackId) fallbackId = variantId;
    }

    return fallbackId;
  }

  waitForEventWithTimeout(target, eventName, timeoutMs = 300) {
    if (!target || !eventName) return Promise.resolve(false);
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;

      const cleanup = () => {
        if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }
        target.removeEventListener(eventName, onEvent);
      };

      const onEvent = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(true);
      };

      target.addEventListener(eventName, onEvent, { once: true });
      timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(false);
      }, Math.max(1, parseInt(timeoutMs, 10) || 300));
    });
  }

  waitForCondition(checkFn, timeoutMs = 260) {
    if (typeof checkFn !== 'function') return Promise.resolve(false);
    const timeout = Math.max(1, parseInt(timeoutMs, 10) || 260);
    return new Promise((resolve) => {
      const start = performance.now();

      const tick = () => {
        let passed = false;
        try {
          passed = !!checkFn();
        } catch (_error) {
          passed = false;
        }

        if (passed) {
          resolve(true);
          return;
        }

        if ((performance.now() - start) >= timeout) {
          resolve(false);
          return;
        }

        window.requestAnimationFrame(tick);
      };

      tick();
    });
  }

  forceCloseQuickViewModal(modal) {
    if (!modal) return;
    modal.removeAttribute('active');
    modal.removeAttribute('open');
    modal.setAttribute('inert', '');
    modal.hidden = true;
  }

  unlockBodyScrollIfNoModals() {
    const body = document.body;
    if (!body) return;
    if (!body.classList.contains('has-modal-open') && !body.classList.contains('has-modal-opening')) return;

    const openModal = document.querySelector(
      '.drawer[open], .drawer[active], [aria-modal="true"][open], [aria-modal="true"][active]'
    );
    if (openModal) return;

    body.classList.remove('has-modal-open', 'has-modal-opening');
  }

  async closeQuickViewWithThemeAnimation() {
    if (!this.mobileQuickView) return;

    const modal = this.mobileQuickView;
    const OPEN_SETTLE_TIMEOUT_MS = 260;
    const HIDE_FALLBACK_TIMEOUT_MS = 1100;
    const POST_CLOSE_GUARD_MS = 260;
    const isDrawerOpen = () => !!modal.open || modal.hasAttribute('open') || modal.hasAttribute('active');
    if (!isDrawerOpen()) {
      this.setQuickViewOpenState(false);
      this.setQuickViewClosingGuard(false);
      this.unlockBodyScrollIfNoModals();
      return;
    }
    this.setQuickViewClosingGuard(true);

    // Theme modal sets `open` immediately and `active` shortly after (~75ms).
    // On very fast taps, closing before `active` can cause a visual open-close glitch.
    if (modal.hasAttribute('open') && !modal.hasAttribute('active')) {
      await this.waitForCondition(
        () => modal.hasAttribute('active') || !modal.hasAttribute('open'),
        OPEN_SETTLE_TIMEOUT_MS
      );
    }

    if (typeof modal.hide === 'function') {
      try {
        const hidePromise = modal.hide();
        const hideResult = hidePromise && typeof hidePromise.then === 'function'
          ? await Promise.race([
            hidePromise.then(() => true).catch(() => false),
            new Promise((resolve) => window.setTimeout(() => resolve(false), HIDE_FALLBACK_TIMEOUT_MS)),
          ])
          : await this.waitForEventWithTimeout(modal, 'modal:afterHide', HIDE_FALLBACK_TIMEOUT_MS);

        if (!hideResult && isDrawerOpen()) {
          this.forceCloseQuickViewModal(modal);
        }
        this.setQuickViewOpenState(false);
        this.setQuickViewClosingGuard(true, POST_CLOSE_GUARD_MS);
        this.unlockBodyScrollIfNoModals();
        return;
      } catch (_error) {
        // Fall back to close button + event wait path below.
      }
    }

    const closeBtn = modal.querySelector('.drawer__close[aria-controls]');
    if (closeBtn) closeBtn.click();

    if (isDrawerOpen()) {
      const didHide = await this.waitForEventWithTimeout(modal, 'modal:afterHide', HIDE_FALLBACK_TIMEOUT_MS);
      if (!didHide && isDrawerOpen()) {
        this.forceCloseQuickViewModal(modal);
      }
    }
    this.setQuickViewOpenState(false);
    this.setQuickViewClosingGuard(true, POST_CLOSE_GUARD_MS);
    this.unlockBodyScrollIfNoModals();
  }

  async handleMobileQuickViewSelect() {
    if (!this.mobileQuickView) return;
    if (this._mobileQuickViewSelectInFlight) return;

    const handle = this.mobileQuickView.dataset.rc2SelectedHandle;
    if (!handle) return;

    this._mobileQuickViewSelectInFlight = true;
    this.setQuickViewSelectButtonsDisabled(true);
    if (this.mobileQuickViewSelectBtn) {
      this.mobileQuickViewSelectBtn.textContent = 'Processing...';
    }
    const desktopSelectBtn = this.mobileQuickView.querySelector('[data-rc2-desktop-select]');
    if (desktopSelectBtn) {
      desktopSelectBtn.textContent = 'Processing...';
    }

    try {
      const selectedStep = parseInt(this.mobileQuickView.dataset.rc2SelectedStep, 10);
      const currentStep = Number.isFinite(selectedStep) && selectedStep > 0
        ? selectedStep
        : this.state.currentStep;
      const quickViewVariantId = this.getQuickViewSelectedVariantId();
      const gridSelector = currentStep === 2 ? '#RC2SettingsGrid' : '#RC2Grid';
      const card = this.findCardByHandle(gridSelector, handle);
      if (!card) {
        await this.closeQuickViewWithThemeAnimation();
        return;
      }

      await this.handleCardClick(card, {
        suppressQuickView: true,
        preferredVariantId: quickViewVariantId,
      });

      await this.closeQuickViewWithThemeAnimation();
      if (this._quickViewOpenTimer) {
        window.clearTimeout(this._quickViewOpenTimer);
        this._quickViewOpenTimer = null;
      }
      this._pendingQuickViewHandle = '';
      this._pendingQuickViewStep = 0;
      if (currentStep === 1 && this.canGoToStep(2)) {
        this.goToStep(2);
      } else if (currentStep === 2 && this.canGoToStep(3)) {
        this.goToStep(3);
      }
      this.mobileQuickView.dataset.rc2SelectedHandle = '';
      this.mobileQuickView.dataset.rc2SelectedStep = '';
    } finally {
      this._mobileQuickViewSelectInFlight = false;
      this.setQuickViewSelectButtonsDisabled(false);
      this.updateQuickViewSelectButtons();
    }
  }

  applyThemeContext(url) {
    if (!url) return;
    const contextParams = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      const value = contextParams.get(key);
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });
  }

  parseShapeFilterParams(rawValue = '') {
    return String(rawValue || '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '');
  }

  normalizeShapeKey(value = '') {
    const rawValue = String(value || '').trim().toLowerCase();
    if (!rawValue) return '';

    const localizedAliases = {
      'ラウンド': 'round',
      '円形': 'round',
      'ポルトギース': 'portuguese',
      'ポルトガル': 'portuguese',
      'オーバル': 'oval',
      'ペア': 'pear',
      'ペア-ティアドロップ': 'pear',
      'ペア／ティアドロップ': 'pear',
      'ティアドロップ': 'pear',
      '洋梨': 'pear',
      'プリンセス': 'princess',
      'エメラルド': 'emerald',
      'マーキス': 'marquise',
      '侯爵夫人': 'marquise',
      'クッション': 'cushion',
      'ハート': 'heart',
      '心臓': 'heart',
      'ラディアント': 'radiant',
      '輝く': 'radiant',
      'アッシャー': 'asscher',
    };
    if (localizedAliases[rawValue]) return localizedAliases[rawValue];

    const normalized = rawValue
      .trim()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!normalized) return '';

    const aliases = {
      'round-brilliant': 'round',
      'brilliant-round': 'round',
      'pear-shaped': 'pear',
      'teardrop': 'pear',
      'teardrop-pear': 'pear',
      'cushion-modified': 'cushion',
      'emerald-cut': 'emerald',
      'princess-cut': 'princess',
      'radiant-cut': 'radiant',
      'asscher-cut': 'asscher',
      'marquise-cut': 'marquise',
      'heart-cut': 'heart',
      'oval-cut': 'oval',
      'round-cut': 'round',
      'portuguese-cut': 'portuguese',
    };

    return aliases[normalized] || normalized;
  }

  getShapeKeyFromText(text = '') {
    const normalizedText = String(text || '').toLowerCase();
    if (!normalizedText) return '';

    const shapePatterns = [
      { key: 'round', regex: /\bround\b|ラウンド|円形/i },
      { key: 'portuguese', regex: /\bportuguese\b|ポルトギース|ポルトガル/i },
      { key: 'oval', regex: /\boval\b|オーバル/i },
      { key: 'pear', regex: /\bpear\b|\bteardrop\b|ペア|ティアドロップ|洋梨/i },
      { key: 'princess', regex: /\bprincess\b|プリンセス/i },
      { key: 'emerald', regex: /\bemerald\b|エメラルド/i },
      { key: 'marquise', regex: /\bmarquise\b|マーキス|侯爵夫人/i },
      { key: 'cushion', regex: /\bcushion\b|クッション/i },
      { key: 'heart', regex: /\bheart\b|ハート|心臓/i },
      { key: 'radiant', regex: /\bradiant\b|ラディアント|輝く/i },
      { key: 'asscher', regex: /\basscher\b|アッシャー/i },
    ];

    const matched = shapePatterns.find((entry) => entry.regex.test(normalizedText));
    return matched ? matched.key : '';
  }

  getSelectedDiamondShapeKey() {
    const storedShape = this.normalizeShapeKey(this.state.selectedDiamond?.shape || '');
    if (storedShape) return storedShape;
    return this.getShapeKeyFromText(this.state.selectedDiamond?.title || '');
  }

  getSelectedDiamondShapeLabel() {
    const shapeKey = this.getSelectedDiamondShapeKey();
    if (!shapeKey) return 'selected diamond';
    return shapeKey
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  getCardShapeKey(card) {
    if (!card?.dataset) return '';

    const shapeValues = String(card.dataset.shape || '').split(',');
    for (const value of shapeValues) {
      const normalized = this.normalizeShapeKey(value);
      if (this.isKnownShapeKey(normalized)) return normalized;
      const inferred = this.getShapeKeyFromText(value);
      if (inferred) return inferred;
    }

    const tags = String(card.dataset.productTags || '').split(',');
    for (const tag of tags) {
      const normalized = this.normalizeShapeKey(tag);
      if (this.isKnownShapeKey(normalized)) return normalized;
      const inferred = this.getShapeKeyFromText(tag);
      if (inferred) return inferred;
    }

    return this.getShapeKeyFromText(card.dataset.productTitle || '');
  }

  isKnownShapeKey(shapeKey = '') {
    return [
      'round',
      'portuguese',
      'oval',
      'pear',
      'princess',
      'emerald',
      'marquise',
      'cushion',
      'heart',
      'radiant',
      'asscher',
    ].includes(String(shapeKey || ''));
  }

  shouldApplyShapeMatching() {
    return this.shapeMatchingEnabled && !this.shapeFilterDisabled;
  }

  getActiveSettingsShapeKey() {
    if (!this.shouldApplyShapeMatching()) return '';
    return this.getSelectedDiamondShapeKey();
  }

  removeShapeFilterParamsFromUrl(url) {
    if (!(url instanceof URL)) return;
    if (!Array.isArray(this.settingsShapeFilterParams)) return;
    this.settingsShapeFilterParams.forEach((param) => {
      if (!param) return;
      url.searchParams.delete(param);
    });
  }

  applyShapeFilterParamsToUrl(url, shapeKey = this.getActiveSettingsShapeKey()) {
    if (!(url instanceof URL)) return '';
    this.removeShapeFilterParamsFromUrl(url);

    const normalizedShape = this.normalizeShapeKey(shapeKey);
    if (!normalizedShape) return '';
    if (!Array.isArray(this.settingsShapeFilterParams) || this.settingsShapeFilterParams.length === 0) {
      return normalizedShape;
    }

    // First key wins: prevent AND-ing multiple filter params which can over-restrict results.
    const primaryParam = this.settingsShapeFilterParams.find((param) => param);
    if (primaryParam) {
      url.searchParams.append(primaryParam, normalizedShape);
    }
    return normalizedShape;
  }

  getShapeOptionIndex(optionNames = []) {
    if (!Array.isArray(optionNames)) return -1;
    return optionNames.findIndex((optName) => {
      const label = typeof optName === 'string' ? optName : optName?.name || '';
      return this.isShapeOptionName(label);
    });
  }

  isMetalOptionName(optionName = '') {
    const normalized = String(optionName || '').trim().toLowerCase();
    return normalized.includes('metal')
      || normalized.includes('material')
      || normalized.includes('金属')
      || normalized.includes('メタル')
      || normalized === '素材';
  }

  isCaratOptionName(optionName = '') {
    const normalized = String(optionName || '').trim().toLowerCase();
    if (!normalized) return false;
    return normalized === 'carat'
      || normalized.includes('carat')
      || normalized === 'ct'
      || normalized.endsWith(' ct')
      || normalized.endsWith(' cts')
      || normalized.includes('カラット')
      || normalized.includes('キャラット');
  }

  isShapeOptionName(optionName = '') {
    const normalized = String(optionName || '').trim().toLowerCase();
    return normalized.includes('shape')
      || normalized.includes('stone')
      || normalized.includes('形状')
      || normalized.includes('シェイプ')
      || normalized.includes('石の形');
  }

  isSizeOptionName(optionName = '') {
    const normalized = String(optionName || '').trim().toLowerCase();
    return normalized.includes('size')
      || normalized.includes('サイズ')
      || normalized === '号数';
  }

  getCaratOptionIndex(optionNames = []) {
    if (!Array.isArray(optionNames)) return -1;
    return optionNames.findIndex((optName) => {
      const label = typeof optName === 'string' ? optName : optName?.name || '';
      return this.isCaratOptionName(label);
    });
  }

  getOptionNameFromList(optionNames = [], optionIndex = -1) {
    if (!Array.isArray(optionNames) || optionIndex < 0) return '';
    const option = optionNames[optionIndex];
    return typeof option === 'string' ? option : option?.name || '';
  }

  hideInternalCaratOptionFieldsets(scope, optionNames = []) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    this.formatInternalCaratOptionLabels(scope, optionNames);

    const fieldsets = scope.querySelectorAll('fieldset, .variant-picker__option');
    fieldsets.forEach((fieldset) => {
      let isCaratFieldset = false;
      const optionIndex = this.getPickerFieldsetOptionIndex(fieldset);
      const optionName = this.getOptionNameFromList(optionNames, optionIndex);

      if (this.isCaratOptionName(optionName)) {
        isCaratFieldset = true;
      }

      if (!isCaratFieldset) {
        const optionSlug = String(fieldset?.dataset?.optionSlug || '').replace(/-/g, ' ');
        if (this.isCaratOptionName(optionSlug)) {
          isCaratFieldset = true;
        }
      }

      if (!isCaratFieldset) {
        const labelText =
          fieldset.querySelector('.form__label')?.textContent
          || fieldset.querySelector('legend')?.textContent
          || '';
        if (this.isCaratOptionName(labelText)) {
          isCaratFieldset = true;
        }
      }

      if (!isCaratFieldset) return;

      fieldset.hidden = true;
      fieldset.setAttribute('aria-hidden', 'true');
      fieldset.classList.add('rc2-variant-option--internal');
    });
  }

  parseCaratValue(value = '') {
    const normalized = String(value || '').replace(/,/g, ' ').trim();
    if (!normalized) return null;

    const strictMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(?:ct|cts|carat|carats)\b/i);
    if (strictMatch && strictMatch[1]) {
      const parsedStrict = parseFloat(strictMatch[1]);
      return Number.isFinite(parsedStrict) ? parsedStrict : null;
    }

    const looseMatch = normalized.match(/(\d+(?:\.\d+)?)/);
    if (!looseMatch || !looseMatch[1]) return null;

    const parsedLoose = parseFloat(looseMatch[1]);
    return Number.isFinite(parsedLoose) ? parsedLoose : null;
  }

  formatCaratBucketLabel(value = '') {
    const parsed = this.parseCaratValue(value);
    if (parsed === null) return String(value || '');
    const normalizedNumber = Number.isInteger(parsed)
      ? String(parsed)
      : String(parsed).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    return `${normalizedNumber}ct`;
  }

  formatInternalCaratOptionLabels(scope, optionNames = []) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    const fieldsets = scope.querySelectorAll('fieldset, .variant-picker__option');
    fieldsets.forEach((fieldset) => {
      const optionIndex = this.getPickerFieldsetOptionIndex(fieldset);
      const optionName = this.getOptionNameFromList(optionNames, optionIndex);
      const optionSlug = String(fieldset?.dataset?.optionSlug || '').replace(/-/g, ' ');
      const labelText =
        fieldset.querySelector('.form__label')?.textContent
        || fieldset.querySelector('legend')?.textContent
        || '';
      if (
        !this.isCaratOptionName(optionName)
        && !this.isCaratOptionName(optionSlug)
        && !this.isCaratOptionName(labelText)
      ) {
        return;
      }

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const displayValue = this.formatCaratBucketLabel(input.value);
        input.dataset.rcCaratLabel = displayValue;
        const label = input.closest('magnet-element')?.querySelector('label')
          || (input.id ? fieldset.querySelector(`label[for="${input.id}"]`) : null);
        if (!label) return;
        label.title = displayValue;
        const tooltip = label.querySelector('.tooltip');
        if (tooltip) {
          tooltip.textContent = displayValue;
        } else {
          label.textContent = displayValue;
        }
      });

      fieldset.querySelectorAll('select option').forEach((option) => {
        option.textContent = this.formatCaratBucketLabel(option.value);
      });
    });
  }

  getSelectedDiamondCaratValue() {
    const diamond = this.state.selectedDiamond || {};
    const sources = [
      diamond.carat,
      diamond.spec,
      diamond.title,
      diamond.variantTitle,
    ];

    for (const source of sources) {
      const parsed = this.parseCaratValue(source);
      if (parsed !== null) return parsed;
    }

    return null;
  }

  getVariantOptionValue(variant, optionIndex) {
    if (!variant || !Array.isArray(variant.options)) return '';
    return variant.options[optionIndex] || '';
  }

  getVariantCaratBucketValue(variant, optionNames = []) {
    const caratOptionIndex = this.getCaratOptionIndex(optionNames);
    if (caratOptionIndex < 0) return null;
    return this.parseCaratValue(this.getVariantOptionValue(variant, caratOptionIndex));
  }

  getPickerFieldsetOptionIndex(fieldset) {
    const raw = fieldset?.dataset?.optionIndex || '';
    const parsed = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed - 1 : -1;
  }

  syncInternalSettingCaratByValue(picker, optionNames = [], options = {}) {
    if (!picker) return false;
    const caratOptionIndex = this.getCaratOptionIndex(optionNames);
    const diamondCarat = this.getSelectedDiamondCaratValue();
    if (caratOptionIndex < 0 || diamondCarat === null) return false;

    const roundedTarget = Math.max(1, Math.round(diamondCarat));
    const fieldsets = picker.querySelectorAll('fieldset, .variant-picker__option');
    let didResolve = false;
    let didAdjustValue = false;
    let nextChangeTarget = null;

    fieldsets.forEach((fieldset) => {
      if (this.getPickerFieldsetOptionIndex(fieldset) !== caratOptionIndex) return;

      this.formatInternalCaratOptionLabels(fieldset.parentElement || picker, optionNames);
      fieldset.hidden = true;
      fieldset.setAttribute('aria-hidden', 'true');
      fieldset.classList.add('rc2-variant-option--internal');

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const rankedRadios = radios
        .map((input) => ({ input, bucket: this.parseCaratValue(input.value) }))
        .filter((entry) => entry.bucket !== null)
        .sort((a, b) => {
          const distance = Math.abs(a.bucket - roundedTarget) - Math.abs(b.bucket - roundedTarget);
          if (distance !== 0) return distance;
          return b.bucket - a.bucket;
        });
      const nextRadio = rankedRadios[0]?.input;
      if (nextRadio) {
        const currentChecked = fieldset.querySelector('input[type="radio"]:checked');
        radios.forEach((input) => {
          input.checked = input === nextRadio;
        });
        didResolve = true;
        if (currentChecked !== nextRadio) {
          didAdjustValue = true;
          nextChangeTarget = nextRadio;
        }
      }

      const select = fieldset.querySelector('select');
      if (select) {
        const rankedOptions = Array.from(select.options)
          .map((option) => ({ option, bucket: this.parseCaratValue(option.value) }))
          .filter((entry) => entry.bucket !== null)
          .sort((a, b) => {
            const distance = Math.abs(a.bucket - roundedTarget) - Math.abs(b.bucket - roundedTarget);
            if (distance !== 0) return distance;
            return b.bucket - a.bucket;
          });
        if (rankedOptions[0]) {
          if (String(select.value) !== String(rankedOptions[0].option.value)) {
            didAdjustValue = true;
            nextChangeTarget = select;
          }
          select.value = rankedOptions[0].option.value;
          didResolve = true;
        }
      }
    });

    if (options.dispatchChange && didAdjustValue && nextChangeTarget) {
      nextChangeTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return didResolve;
  }

  getPickerSelectedOptionValueIds(picker) {
    if (!picker || typeof picker.querySelectorAll !== 'function') return [];
    const fieldsets = Array.from(picker.querySelectorAll('fieldset, .variant-picker__option'));
    return fieldsets
      .map((fieldset) => {
        const selected = fieldset.querySelector('input[type="radio"]:checked')
          || fieldset.querySelector('select option:checked')
          || fieldset.querySelector('select option[selected]');
        return String(selected?.dataset?.optionValueId || '').trim();
      })
      .filter(Boolean);
  }

  getVariantResolutionUrl(handle, optionValueIds = [], origin = window.location.origin, root = window.Shopify?.routes?.root || '/') {
    const normalizedHandle = String(handle || '').trim();
    const normalizedRoot = `/${String(root || '/').replace(/^\/+|\/+$/g, '')}/`.replace(/^\/\/$/, '/');
    const url = new URL(`${normalizedRoot}products/${encodeURIComponent(normalizedHandle)}`, origin);
    url.searchParams.set('section_id', this.sectionPreviewId || 'rc-product-preview');
    url.searchParams.set('option_values', optionValueIds.join(','));
    return url;
  }

  async resolveVariantFromOptionValues(picker, optionNames = [], requestOptions = {}) {
    const handle = this.state.selectedSetting?.handle || '';
    if (!handle || !picker) return null;

    const optionValueIds = this.getPickerSelectedOptionValueIds(picker);
    const expectedCount = Array.isArray(optionNames) && optionNames.length > 0
      ? optionNames.length
      : picker.querySelectorAll('fieldset, .variant-picker__option').length;
    if (expectedCount === 0 || optionValueIds.length !== expectedCount) return null;

    const url = this.getVariantResolutionUrl(handle, optionValueIds);
    this.applyThemeContext(url);

    const response = await fetch(url.toString(), {
      signal: requestOptions.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
      throw new Error(`Variant resolution request failed: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const selectedVariantScript = doc.querySelector('[data-selected-variant]');
    if (!selectedVariantScript?.textContent) return null;

    const variant = JSON.parse(selectedVariantScript.textContent);
    if (!variant || !variant.id || variant.available === false) return null;
    return this.doesVariantMatchPickerSelection(variant, picker) ? variant : null;
  }

  doesVariantMatchPickerSelection(variant, picker) {
    if (!variant || !Array.isArray(variant.options) || !picker) return false;
    const fieldsets = Array.from(picker.querySelectorAll('fieldset, .variant-picker__option'));
    if (fieldsets.length === 0) return false;

    return fieldsets.every((fieldset) => {
      const optionIndex = this.getPickerFieldsetOptionIndex(fieldset);
      if (optionIndex < 0) return true;
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      const select = fieldset.querySelector('select');
      const selectedValue = selected?.value || select?.value || '';
      if (!selectedValue) return false;
      return String(variant.options[optionIndex] || '').toLowerCase() === String(selectedValue).toLowerCase();
    });
  }

  getPickerSelectedOptionConstraints(picker, optionNames = []) {
    const constraints = new Map();
    if (!picker) return constraints;

    const fieldsets = picker.querySelectorAll('fieldset, .variant-picker__option');
    fieldsets.forEach((fieldset) => {
      const optionIndex = this.getPickerFieldsetOptionIndex(fieldset);
      if (optionIndex < 0) return;

      const optionName = typeof optionNames[optionIndex] === 'string'
        ? optionNames[optionIndex]
        : optionNames[optionIndex]?.name || '';

      if (this.isCaratOptionName(optionName)) return;

      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      const select = fieldset.querySelector('select');
      const value = checkedRadio?.value || select?.value || '';
      if (!value) return;

      constraints.set(optionIndex, String(value).toLowerCase());
    });

    return constraints;
  }

  findCaratBucketMatchedVariant(variants = [], optionNames = [], diamondCarat = this.getSelectedDiamondCaratValue(), options = {}) {
    const caratOptionIndex = this.getCaratOptionIndex(optionNames);
    const targetCarat = this.parseCaratValue(diamondCarat);
    if (caratOptionIndex < 0 || targetCarat === null) return null;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const roundedBucketTarget = Math.max(1, Math.round(targetCarat));

    const normalizedShape = this.normalizeShapeKey(options.shapeKey || this.getSelectedDiamondShapeKey());
    const shapeOptionIndex = this.getShapeOptionIndex(optionNames);
    const selectedConstraints = options.selectedConstraints instanceof Map ? options.selectedConstraints : new Map();

    const rankCandidates = (candidateVariants) => candidateVariants
      .map((variant) => ({
        variant,
        bucket: this.getVariantCaratBucketValue(variant, optionNames),
      }))
      .filter((entry) => entry.bucket !== null)
      .sort((a, b) => {
        const aDistance = Math.abs(a.bucket - roundedBucketTarget);
        const bDistance = Math.abs(b.bucket - roundedBucketTarget);
        if (aDistance !== bDistance) return aDistance - bDistance;

        const aExact = a.bucket === roundedBucketTarget ? 0 : 1;
        const bExact = b.bucket === roundedBucketTarget ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;

        // If the exact rounded bucket does not exist, prefer the larger bucket on ties.
        return b.bucket - a.bucket;
      });

    const buildCandidates = (respectAvailability) => variants.filter((variant) => {
      if (!variant || !Array.isArray(variant.options)) return false;
      if (respectAvailability && variant.available === false) return false;

      const bucket = this.getVariantCaratBucketValue(variant, optionNames);
      if (bucket === null) return false;

      if (normalizedShape && shapeOptionIndex >= 0) {
        const variantShapeKey = this.normalizeShapeKey(this.getVariantOptionValue(variant, shapeOptionIndex));
        if (variantShapeKey && variantShapeKey !== normalizedShape) return false;
      }

      for (const [optionIndex, expectedValue] of selectedConstraints.entries()) {
        if (optionIndex === caratOptionIndex) continue;
        const actualValue = String(this.getVariantOptionValue(variant, optionIndex)).toLowerCase();
        if (actualValue !== expectedValue) return false;
      }

      return true;
    });

    const availableCandidates = rankCandidates(buildCandidates(true));
    if (availableCandidates[0]) return availableCandidates[0].variant;

    const fallbackCandidates = rankCandidates(buildCandidates(false));
    return fallbackCandidates[0]?.variant || null;
  }

  getAutoMatchedSettingVariant(variants = [], optionNames = [], options = {}) {
    const diamondShapeKey = this.getSelectedDiamondShapeKey();
    const diamondCarat = this.getSelectedDiamondCaratValue();
    const selectedConstraints = options.selectedConstraints instanceof Map ? options.selectedConstraints : null;
    const fallbackToShape = options.fallbackToShape !== false;

    const caratMatchedVariant = this.findCaratBucketMatchedVariant(variants, optionNames, diamondCarat, {
      shapeKey: diamondShapeKey,
      selectedConstraints,
    });
    if (caratMatchedVariant) return caratMatchedVariant;

    if (!fallbackToShape) return null;
    return this.findShapeMatchedVariant(variants, optionNames, diamondShapeKey);
  }

  syncInternalSettingCaratOption(picker, variant, optionNames = [], options = {}) {
    if (!picker || !variant) return;

    const caratOptionIndex = this.getCaratOptionIndex(optionNames);
    if (caratOptionIndex < 0) return;

    let nextChangeTarget = null;
    let didAdjustValue = false;

    const fieldsets = picker.querySelectorAll('fieldset, .variant-picker__option');
    fieldsets.forEach((fieldset) => {
      const optionIndex = this.getPickerFieldsetOptionIndex(fieldset);
      if (optionIndex !== caratOptionIndex) return;

      const nextValue = this.getVariantOptionValue(variant, optionIndex);
      fieldset.hidden = true;
      fieldset.setAttribute('aria-hidden', 'true');
      fieldset.classList.add('rc2-variant-option--internal');

      const checkedRadio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => String(input.value) === String(nextValue));
      if (checkedRadio) {
        const currentChecked = fieldset.querySelector('input[type="radio"]:checked');
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input === checkedRadio;
        });
        if (currentChecked !== checkedRadio) {
          nextChangeTarget = checkedRadio;
          didAdjustValue = true;
        }
      }

      const select = fieldset.querySelector('select');
      if (select) {
        if (String(select.value) !== String(nextValue)) {
          nextChangeTarget = select;
          didAdjustValue = true;
        }
        select.value = nextValue;
      }
    });

    const syncScope = options.scope && typeof options.scope.querySelectorAll === 'function'
      ? options.scope
      : picker.closest('.product') || picker.parentElement || picker;
    if (syncScope && variant.id) {
      syncScope.querySelectorAll('input[name="id"], select[name="id"]').forEach((input) => {
        if (String(input.value) !== String(variant.id)) {
          input.value = variant.id;
        }
      });
    }

    if (options.dispatchChange && didAdjustValue && nextChangeTarget) {
      nextChangeTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  applyInternalSettingOptionsUi(container) {
    if (!container || this.state.currentStep !== 2) return;

    const picker = container.querySelector('variant-picker');
    const optionNames = this.state.selectedSetting?.options || [];
    if (!picker || !Array.isArray(optionNames) || optionNames.length === 0) return;

    this.syncInternalSettingCaratByValue(picker, optionNames);
    this.hideInternalCaratOptionFieldsets(picker, optionNames);
    if (this.doesVariantMatchPickerSelection(this.state.selectedVariant, picker)) {
      this.syncInternalSettingCaratOption(picker, this.state.selectedVariant, optionNames);
    }
  }

  getHandleFromProductUrl(productUrl = '') {
    const normalizedUrl = String(productUrl || '').trim();
    if (!normalizedUrl) return '';

    try {
      const url = new URL(normalizedUrl, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match?.[1] ? decodeURIComponent(match[1]) : '';
    } catch (_error) {
      const match = normalizedUrl.match(/\/products\/([^/?#]+)/i);
      return match?.[1] ? decodeURIComponent(match[1]) : '';
    }
  }

  async applyQuickViewInternalSettingOptions(container) {
    if (!container || this.state.currentStep !== 2 || !this.state.selectedDiamond) return;

    const picker = container.querySelector('variant-picker');
    if (!picker) return;
    picker.removeAttribute('data-dynamic-variants');

    const handle = this.getHandleFromProductUrl(this.mobileQuickView?.getAttribute('data-product-url') || '');
    if (!handle) return;

    try {
      const productData = await this.fetchProductJSON(handle);
      if (!productData || !Array.isArray(productData.variants) || !Array.isArray(productData.options)) return;

      const optionNames = productData.options;
      if (this.getCaratOptionIndex(optionNames) < 0) return;

      this.hideInternalCaratOptionFieldsets(container, optionNames);
      this.syncInternalSettingCaratByValue(picker, optionNames, {
        dispatchChange: true,
      });
    } catch (error) {
      console.warn('Could not sync quick view internal setting options:', error);
    }
  }

  getVariantShapeKey(variant, optionNames = []) {
    if (!variant || !Array.isArray(variant.options)) return '';
    const shapeOptionIndex = this.getShapeOptionIndex(optionNames);
    if (shapeOptionIndex < 0) return '';
    return this.normalizeShapeKey(variant.options[shapeOptionIndex]);
  }

  findShapeMatchedVariant(variants = [], optionNames = [], shapeKey = this.getSelectedDiamondShapeKey()) {
    const normalizedShape = this.normalizeShapeKey(shapeKey);
    if (!normalizedShape) return null;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const shapeOptionIndex = this.getShapeOptionIndex(optionNames);
    if (shapeOptionIndex < 0) return null;

    const availableMatch = variants.find((variant) => {
      if (!variant || variant.available === false || !Array.isArray(variant.options)) return false;
      const variantShapeKey = this.normalizeShapeKey(variant.options[shapeOptionIndex]);
      return variantShapeKey && variantShapeKey === normalizedShape;
    });
    if (availableMatch) return availableMatch;

    return variants.find((variant) => {
      if (!variant || !Array.isArray(variant.options)) return false;
      const variantShapeKey = this.normalizeShapeKey(variant.options[shapeOptionIndex]);
      return variantShapeKey && variantShapeKey === normalizedShape;
    }) || null;
  }

  getSelectedSettingShapeKey() {
    const fromState = this.normalizeShapeKey(this.state.selectedStoneShape || '');
    if (fromState) return fromState;
    const fromVariant = this.getVariantShapeKey(this.state.selectedVariant, this.state.selectedSetting?.options || []);
    if (fromVariant) return fromVariant;
    return this.getShapeKeyFromText(this.state.selectedSetting?.title || '');
  }

  isSelectedSettingCompatibleWithDiamond() {
    const diamondShape = this.getSelectedDiamondShapeKey();
    if (!diamondShape) return true;
    const settingShape = this.getSelectedSettingShapeKey();
    if (!settingShape) return true;
    return settingShape === diamondShape;
  }

  clearSettingSelection(options = {}) {
    const resetGridSelection = options.resetGridSelection !== false;
    this.state.selectedSetting = null;
    this.state.selectedVariant = null;
    this.state.selectedMetal = null;
    this.state.selectedStoneShape = null;
    this.state.selectedSettingCaratBucket = null;
    this.state.selectedRingSize = null;

    if (!resetGridSelection) return;
    const settingsGrid = this.querySelector('#RC2SettingsGrid');
    if (!settingsGrid) return;
    settingsGrid.querySelectorAll('.rc2-card.is-selected').forEach((card) => {
      card.classList.remove('is-selected');
      const btn = card.querySelector('.rc2-card__btn');
      if (!btn) return;
      const isAvailable = card.dataset.productAvailable !== 'false';
      btn.textContent = isAvailable ? 'Select' : 'Out of stock';
    });
  }

  doesSettingCardMatchShape(card, shapeKey) {
    if (!card || !shapeKey) return true;
    const normalizedShape = this.normalizeShapeKey(shapeKey);
    if (!normalizedShape) return true;

    const cardShapes = String(card.dataset.shape || '')
      .split(',')
      .map((value) => this.normalizeShapeKey(value) || this.getShapeKeyFromText(value))
      .filter((value) => this.isKnownShapeKey(value));
    if (cardShapes.length > 0) {
      return cardShapes.includes(normalizedShape);
    }

    const tagList = (card.dataset.productTags || '')
      .split(',')
      .map((tag) => this.normalizeShapeKey(tag) || this.getShapeKeyFromText(tag))
      .filter((value) => this.isKnownShapeKey(value));
    if (tagList.length > 0) {
      return tagList.includes(normalizedShape);
    }

    const titleShape = this.getShapeKeyFromText(card.dataset.productTitle || '');
    if (titleShape) {
      return this.normalizeShapeKey(titleShape) === normalizedShape;
    }

    return false;
  }

  applyClientSideSettingsShapeFilter(grid, shapeKey) {
    if (!grid) return { filtered: false, visibleCount: 0, totalCount: 0 };

    const cards = Array.from(grid.querySelectorAll('.rc2-card'));
    const totalCount = cards.length;
    if (!shapeKey || totalCount === 0) {
      cards.forEach((card) => {
        card.hidden = false;
        card.classList.remove('rc2-card--shape-hidden');
      });
      return { filtered: false, visibleCount: totalCount, totalCount };
    }

    const hasShapeHints = cards.some((card) => {
      const hasDatasetShape = String(card.dataset.shape || '')
        .split(',')
        .map((value) => this.normalizeShapeKey(value) || this.getShapeKeyFromText(value))
        .filter((value) => this.isKnownShapeKey(value))
        .length > 0;
      const hasTagShape = String(card.dataset.productTags || '')
        .split(',')
        .map((value) => this.normalizeShapeKey(value) || this.getShapeKeyFromText(value))
        .some((value) => this.isKnownShapeKey(value));
      const hasTitleShape = this.getShapeKeyFromText(card.dataset.productTitle || '') !== '';
      return hasDatasetShape || hasTagShape || hasTitleShape;
    });

    if (!hasShapeHints) {
      cards.forEach((card) => {
        card.hidden = false;
        card.classList.remove('rc2-card--shape-hidden');
      });
      return { filtered: false, visibleCount: totalCount, totalCount };
    }

    let visibleCount = 0;
    cards.forEach((card) => {
      const matches = this.doesSettingCardMatchShape(card, shapeKey);
      card.hidden = !matches;
      card.classList.toggle('rc2-card--shape-hidden', !matches);
      if (matches) visibleCount += 1;
    });

    return { filtered: true, visibleCount, totalCount };
  }

  getShapeMatchCountText(visibleCount, totalCount) {
    const visible = Math.max(0, parseInt(visibleCount, 10) || 0);
    const total = Math.max(0, parseInt(totalCount, 10) || 0);
    const noun = visible === 1 ? 'setting' : 'settings';
    return total > 0
      ? `${visible} matching ${noun} of ${total}`
      : `${visible} matching ${noun}`;
  }

  updateShapeMatchProductCount(visibleCount, totalCount) {
    const productCount = this.querySelector('#ProductCount');
    if (!productCount) return;
    productCount.textContent = this.getShapeMatchCountText(visibleCount, totalCount);
  }

  getNoShapeMatchMarkup(shapeLabel = this.getSelectedDiamondShapeLabel()) {
    return `
      <div class="rc2-empty rc2-empty--shape" data-rc2-no-shape-match>
        <h3>No matching settings for ${this.escapeHtml(shapeLabel)}</h3>
        <p>Try a different diamond shape or show all settings and filter manually.</p>
        <div class="rc2-empty__actions">
          <button type="button" class="button button--secondary" data-rc2-show-all-settings>Show all settings</button>
          <button type="button" class="button button--primary" data-rc2-change-diamond>Change diamond</button>
        </div>
      </div>
    `;
  }

  buildFacetRequestUrl(form, options = {}) {
    const applyShapeMatching = options.applyShapeMatching === true;
    const searchParams = new URLSearchParams(new FormData(form));
    const url = new URL(form.action, window.location.origin);
    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    this.removeShapeFilterParamsFromUrl(url);
    if (applyShapeMatching && this.shouldApplyShapeMatching()) {
      this.applyShapeFilterParamsToUrl(url);
    }

    url.searchParams.set('section_id', this.renderSectionId);
    return url;
  }

  updateHistoryFromFacetUrl(url) {
    const cleanUrl = new URL(url.toString());
    cleanUrl.searchParams.delete('section_id');
    history.replaceState({}, '', cleanUrl.toString());
  }

  handleStep1FacetSubmit(form) {
    const url = this.buildFacetRequestUrl(form, { applyShapeMatching: false });
    if (form.hasAttribute('data-history')) {
      this.updateHistoryFromFacetUrl(url);
    }
    this.loadDiamondCollection(url.toString());
  }

  handleStep1FacetRemove(link) {
    const form = this.querySelector('form[is="facet-form"]');
    if (!form) return;

    const url = new URL(link.href, window.location.origin);
    this.removeShapeFilterParamsFromUrl(url);
    url.searchParams.set('section_id', this.renderSectionId);

    if (form.hasAttribute('data-history')) {
      this.updateHistoryFromFacetUrl(url);
    }
    this.loadDiamondCollection(url.toString());
  }

  handleStep2FacetSubmit(form) {
    if (this.usesManualSettingsProducts) {
      this.hydrateManualSettingsProducts();
      return;
    }

    const url = this.buildFacetRequestUrl(form, { applyShapeMatching: true });
    if (form.hasAttribute('data-history')) {
      this.updateHistoryFromFacetUrl(url);
    }
    this.loadSettingsCollection(url.toString());
  }

  handleStep2FacetRemove(link) {
    if (this.usesManualSettingsProducts) {
      this.hydrateManualSettingsProducts();
      return;
    }

    const form = this.querySelector('form[is="facet-form"]');
    if (!form) return;

    const url = new URL(link.href, window.location.origin);
    this.removeShapeFilterParamsFromUrl(url);
    if (this.shouldApplyShapeMatching()) {
      this.applyShapeFilterParamsToUrl(url);
    }
    url.searchParams.set('section_id', this.renderSectionId);

    if (form.hasAttribute('data-history')) {
      this.updateHistoryFromFacetUrl(url);
    }
    this.loadSettingsCollection(url.toString());
  }

  handleShowAllSettings() {
    if (this.usesManualSettingsProducts) {
      this.shapeFilterDisabled = true;
      this.lastAppliedSettingsShapeKey = '';
      this.clearSettingSelection();
      this.settingsLoaded = false;
      this._settingsFiltersMarkup = '';
      this._settingsCountMarkup = '';
      this.updateSummary();
      this.updateButtons();
      this.updateStepImages();
      this.saveState();
      this.hydrateManualSettingsProducts();
      return;
    }

    if (!this.settingsCollectionHandle) return;

    const root = window.Shopify?.routes?.root || '/';
    const fallbackUrl = `${root}collections/${this.settingsCollectionHandle}`;
    const url = new URL(this.lastSettingsRequestUrl || fallbackUrl, window.location.origin);
    this.removeShapeFilterParamsFromUrl(url);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');

    this.shapeFilterDisabled = true;
    this.lastAppliedSettingsShapeKey = '';
    this.clearSettingSelection();
    this.settingsLoaded = false;
    this._settingsFiltersMarkup = '';
    this._settingsCountMarkup = '';

    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();
    this.saveState();
    if (this.isCollectionPage) {
      history.replaceState({}, '', url.toString());
    }
    this.loadSettingsCollection(url.toString());
  }

  restoreCardSelection(gridSelector, handle) {
    if (!gridSelector || !handle) return null;
    const grid = this.querySelector(gridSelector);
    if (!grid) return null;

    grid.querySelectorAll('.rc2-card.is-selected').forEach((card) => {
      card.classList.remove('is-selected');
      const btn = card.querySelector('.rc2-card__btn');
      if (btn) btn.textContent = 'Select';
    });

    const selectedCard = grid.querySelector(`[data-product-handle="${handle}"]`);
    if (!selectedCard) return null;

    selectedCard.classList.add('is-selected');
    const selectedBtn = selectedCard.querySelector('.rc2-card__btn');
    if (selectedBtn) selectedBtn.textContent = 'Selected \u2713';
    return selectedCard;
  }

  getSelectedCardForStep(step = this.state.currentStep) {
    if (step === 1 && this.state.selectedDiamond?.handle) {
      return this.findCardByHandle('#RC2Grid', this.state.selectedDiamond.handle);
    }
    if (step === 2 && this.state.selectedSetting?.handle) {
      return this.findCardByHandle('#RC2SettingsGrid', this.state.selectedSetting.handle);
    }
    return null;
  }

  scrollElementIntoViewport(element) {
    if (!element) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stickyHeader = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')
    ) || 0;
    const extraOffset = this.isMobileViewport() ? 110 : 20;
    const targetTop = window.scrollY + element.getBoundingClientRect().top - stickyHeader - extraOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  }

  scrollToSelectedCard(step = this.state.currentStep, selectedCard = null) {
    const card = selectedCard || this.getSelectedCardForStep(step);
    if (!card) return false;

    window.requestAnimationFrame(() => this.scrollElementIntoViewport(card));
    return true;
  }

  switchStepPanel(nextStep) {
    const nextPanel = this.querySelector(`.rc2-step-panel[data-panel="${nextStep}"]`);
    if (!nextPanel) return;

    const currentPanel = this.querySelector('.rc2-step-panel.is-visible');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this._panelSwitchTimer) {
      window.clearTimeout(this._panelSwitchTimer);
      this._panelSwitchTimer = null;
    }

    this.panels.forEach((panel) => {
      panel.classList.remove('is-entering', 'is-exiting');
      panel.style.display = '';
      panel.style.visibility = '';
      panel.style.opacity = '';
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transform = '';
      panel.style.clipPath = '';
    });

    if (!currentPanel || currentPanel === nextPanel || reducedMotion) {
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-visible', panel === nextPanel);
      });
      if (this.content) {
        this.content.classList.remove('is-panel-switching');
        this.content.style.removeProperty('--rc2-panel-switch-height');
      }
      return;
    }

    nextPanel.classList.add('is-visible', 'is-entering');
    currentPanel.classList.remove('is-visible');
    currentPanel.classList.add('is-exiting');

    const minSwitchHeight = Math.max(currentPanel.offsetHeight || 0, nextPanel.offsetHeight || 0, 300);
    if (this.content) {
      this.content.classList.add('is-panel-switching');
      this.content.style.setProperty('--rc2-panel-switch-height', `${minSwitchHeight}px`);
    }

    this._panelSwitchTimer = window.setTimeout(() => {
      currentPanel.classList.remove('is-exiting');
      nextPanel.classList.remove('is-entering');
      if (this.content) {
        this.content.classList.remove('is-panel-switching');
        this.content.style.removeProperty('--rc2-panel-switch-height');
      }
      this._panelSwitchTimer = null;
    }, 240);
  }

  goToStep(n) {
    const currentStep = this.state.currentStep;
    if (n === currentStep) return;

    if (this._quickViewOpenTimer) {
      window.clearTimeout(this._quickViewOpenTimer);
      this._quickViewOpenTimer = null;
    }
    this._pendingQuickViewHandle = '';
    this._pendingQuickViewStep = 0;

    this.state.currentStep = n;
    this.dataset.currentStep = n;
    this.triggerStepTransition();
    this.resetPreviewPosition();

    // Update step indicators
    this.stepsEl.forEach(s => {
      const sn = parseInt(s.dataset.step);
      const wasActive = s.classList.contains('is-active');
      const isActive = sn === n;

      s.classList.toggle('is-active', isActive);
      s.classList.toggle('is-completed', sn < n);

      if (isActive && !wasActive) {
        s.classList.remove('is-activating');
        // Force reflow so the activation pulse can replay on rapid step switches.
        void s.offsetWidth;
        s.classList.add('is-activating');
        if (s._rc2ActivateTimer) {
          window.clearTimeout(s._rc2ActivateTimer);
        }
        s._rc2ActivateTimer = window.setTimeout(() => {
          s.classList.remove('is-activating');
          s._rc2ActivateTimer = null;
        }, 320);
      } else if (!isActive) {
        s.classList.remove('is-activating');
        if (s._rc2ActivateTimer) {
          window.clearTimeout(s._rc2ActivateTimer);
          s._rc2ActivateTimer = null;
        }
      }
    });

    // Update connecting lines
    this.lines.forEach(line => {
      const lineKey = line.dataset.line;
      if (lineKey === '1-2') {
        line.classList.toggle('is-completed', n >= 2);
      } else if (lineKey === '2-3') {
        line.classList.toggle('is-completed', n >= 3);
      }
    });

    // Show correct panel with a smooth swap, avoiding blank flashes.
    this.switchStepPanel(n);

    // Update buttons
    this.updateButtons();

    // Show/hide filters based on step (hide on review)
    const filtersWrap = this.querySelector('#RC2FiltersWrap');
    if (filtersWrap) {
      filtersWrap.style.display = n === 3 ? 'none' : '';
    }

    const shouldFocusSelectedCard = (n === 1 && !!this.state.selectedDiamond) || (n === 2 && !!this.state.selectedSetting);
    let focusHandledByStep = false;

    // Step-specific actions
    if (n === 1) {
      this.applyCachedDiamondFilterUi();
      this.activeFilterCollectionHandle = this.diamondCollectionHandle || this.activeFilterCollectionHandle;
      if (this.needsDiamondCollectionRefresh()) {
        this.loadDiamondCollection(null, { focusSelectedCard: shouldFocusSelectedCard });
        focusHandledByStep = shouldFocusSelectedCard;
      } else if (shouldFocusSelectedCard) {
        focusHandledByStep = this.scrollToSelectedCard(1);
      }
    } else if (n === 2) {
      this.applyCachedSettingsFilterUi();
      this.activeFilterCollectionHandle = this.usesManualSettingsProducts
        ? 'manual-settings'
        : this.settingsCollectionHandle || this.activeFilterCollectionHandle;
      if (this.usesManualSettingsProducts) {
        this.hydrateManualSettingsProducts({ focusSelectedCard: shouldFocusSelectedCard });
        focusHandledByStep = shouldFocusSelectedCard;
      } else if (this.needsSettingsCollectionRefresh()) {
        this.loadSettingsCollection(null, { focusSelectedCard: shouldFocusSelectedCard });
        focusHandledByStep = shouldFocusSelectedCard;
      } else if (shouldFocusSelectedCard) {
        focusHandledByStep = this.scrollToSelectedCard(2);
      }
    } else if (n === 3) {
      this.renderReview();
    }

    // Update preview panel
    // On mobile we keep preview hidden and use quick-view drawer.
    if (this.isMobileViewport()) {
      this.clearPreview();
    } else if (n === 1 && this.state.selectedDiamond) {
      this.loadProductGallery(this.state.selectedDiamond.handle);
    } else if (n === 2 && this.state.selectedSetting) {
      this.loadProductGallery(
        this.state.selectedSetting.handle,
        this.state.selectedVariant?.id || this.state.selectedSetting.variantId
      );
    } else if (n === 3) {
      this.clearPreview();
    }

    this.saveState();
    if (!focusHandledByStep) {
      window.requestAnimationFrame(() => this.scrollToTop());
    }
  }

  handleContinue() {
    const step = this.state.currentStep;
    if (step === 1 && this.state.selectedDiamond) {
      this.goToStep(2);
    } else if (step === 2 && this.state.selectedSetting && this.isSelectedSettingCompatibleWithDiamond()) {
      this.goToStep(3);
    } else if (step === 3) {
      this.addToCart();
    }
  }

  handleBack() {
    const step = this.state.currentStep;
    if (step > 1) {
      this.goToStep(step - 1);
    }
  }

  resetCardSelections() {
    this.querySelectorAll('.rc2-card').forEach((card) => {
      card.classList.remove('is-selected');
      const button = card.querySelector('.rc2-card__btn');
      if (!button) return;
      const isAvailable = card.dataset.productAvailable !== 'false';
      button.textContent = isAvailable ? 'Select' : 'Out of stock';
    });
  }

  resetToStepOne() {
    if (this.mobileQuickView?.open && typeof this.mobileQuickView.hide === 'function') {
      this.mobileQuickView.hide();
    }
    this.setQuickViewOpenState(false);
    if (this.mobileQuickView) {
      this.mobileQuickView.dataset.rc2SelectedHandle = '';
      this.mobileQuickView.dataset.rc2SelectedStep = '';
    }

    this.clearState(1);
    this.resetCardSelections();
    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();

    this.state.currentStep = 0;
    this.dataset.currentStep = '0';
    this.goToStep(1);
    this.showNotification('Selection reset. Choose a diamond to start again.', 'info');
  }

  resetAfterSuccessfulAddToCart() {
    if (this.mobileQuickView?.open && typeof this.mobileQuickView.hide === 'function') {
      this.mobileQuickView.hide();
    }
    this.setQuickViewOpenState(false);
    if (this.mobileQuickView) {
      this.mobileQuickView.dataset.rc2SelectedHandle = '';
      this.mobileQuickView.dataset.rc2SelectedStep = '';
    }

    this.clearState(1);
    this.resetCardSelections();
    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();

    this.state.currentStep = 0;
    this.dataset.currentStep = '0';
    this.goToStep(1);
  }

  updateButtons() {
    const step = this.state.currentStep;

    if (this.continueBtn) {
      if (step === 1) {
        this.continueBtn.textContent = this.getStepActionLabel(1);
        this.continueBtn.disabled = !this.state.selectedDiamond;
      } else if (step === 2) {
        const stepTwoReady = !!this.state.selectedSetting && this.isSelectedSettingCompatibleWithDiamond();
        this.continueBtn.textContent = this.getStepActionLabel(2);
        this.continueBtn.disabled = !stepTwoReady;
      } else if (step === 3) {
        this.continueBtn.textContent = this.getStepActionLabel(3);
        this.continueBtn.disabled = !this.isStepThreeActionReady();
      }
    }

    if (this.backBtn) {
      this.backBtn.classList.toggle('rc2-hidden', step === 1);
      if (step === 2) {
        this.backBtn.innerHTML = '&larr; Back to Diamond';
      } else if (step === 3) {
        this.backBtn.innerHTML = '&larr; Back to Setting';
      }
    }

    this.updateQuickViewSelectButtons();
    this.updateMobileStrip();
  }

  /* ===================== SORT DROPDOWN ===================== */
  initSortDropdown() {
    const sortWrapper = this.querySelector('facet-sort');
    if (!sortWrapper) return;

    const hoverElement = sortWrapper.querySelector('hover-element');
    if (!hoverElement) return;

    const sortListbox = sortWrapper.querySelector('.sort-listbox');
    const sortSelection = sortWrapper.querySelector('.sort-selection');
    const sortClose = sortWrapper.querySelector('.sort-close');
    if (!sortListbox) return;

    let isOpen = false;

    const open = () => {
      if (isOpen) return;
      isOpen = true;
      sortListbox.style.opacity = '1';
      sortListbox.style.visibility = 'visible';
      sortListbox.style.pointerEvents = 'auto';
      sortListbox.querySelectorAll('li').forEach(li => {
        li.style.opacity = '1';
      });
      hoverElement.classList.add('is-expanded');
    };

    const close = () => {
      if (!isOpen) return;
      isOpen = false;
      sortListbox.style.opacity = '';
      sortListbox.style.visibility = '';
      sortListbox.style.pointerEvents = '';
      sortListbox.querySelectorAll('li').forEach(li => {
        li.style.opacity = '';
      });
      hoverElement.classList.remove('is-expanded');
    };

    // Toggle on hover-element click (but not on radio/label clicks)
    hoverElement.addEventListener('click', (e) => {
      if (e.target.closest('input[type="radio"]') || e.target.closest('label[for]')) return;
      e.preventDefault();
      e.stopPropagation();
      isOpen ? close() : open();
    });

    // Close button inside sort header
    if (sortClose) {
      sortClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }

    // Handle radio selection
    sortWrapper.addEventListener('change', (e) => {
      if (e.target.name === 'sort_by') {
        if (sortSelection && e.target.nextElementSibling) {
          sortSelection.textContent = e.target.nextElementSibling.textContent;
        }
        close();
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        window.location.href = url.toString();
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (isOpen && !sortWrapper.contains(e.target)) {
        close();
      }
    });
  }

  /* ===================== CARD SELECTION ===================== */
  async handleCardClick(card, options = {}) {
    const suppressQuickView = options?.suppressQuickView === true;
    const preferredVariantId = parseInt(options?.preferredVariantId, 10);
    const hasPreferredVariant = Number.isFinite(preferredVariantId) && preferredVariantId > 0;
    const isAvailable = card.dataset.productAvailable !== 'false';
    if (!isAvailable) {
      this.showNotification('This product is out of stock.', 'error');
      return;
    }

    const step = this.state.currentStep;
    const handle = card.dataset.productHandle;
    const grid = card.closest('.rc2-grid');

    // Deselect others
    if (grid) {
      grid.querySelectorAll('.rc2-card.is-selected').forEach(c => {
        c.classList.remove('is-selected');
        const btn = c.querySelector('.rc2-card__btn');
        if (btn) btn.textContent = 'Select';
      });
    }

    // Select this card
    card.classList.add('is-selected');
    card.classList.remove('is-selecting');
    // Force reflow so selection pulse can replay when changing quickly.
    void card.offsetWidth;
    card.classList.add('is-selecting');
    window.setTimeout(() => {
      card.classList.remove('is-selecting');
    }, 260);

    const btn = card.querySelector('.rc2-card__btn');
    if (btn) btn.textContent = 'Selected \u2713';

    if (step === 1) {
      await this.selectDiamond(card, hasPreferredVariant ? preferredVariantId : null);
    } else if (step === 2) {
      await this.selectSetting(card, hasPreferredVariant ? preferredVariantId : null);
    }

    // Mobile: use quick-view drawer instead of inline preview.
    // Desktop/tablet: keep inline side preview.
    if (this.isMobileViewport()) {
      if (!suppressQuickView) {
        this.openMobileQuickView(card);
      }
      return;
    }

    const previewVariantId = step === 2
      ? (
        this.state.selectedVariant?.id
        || (hasPreferredVariant ? preferredVariantId : null)
        || parseInt(card.dataset.variantId, 10)
      )
      : null;

    this.loadProductGallery(handle, previewVariantId);
  }

  selectDiamond(card, preferredVariantId = null) {
    const previousShapeKey = this.getSelectedDiamondShapeKey();
    const inferredShape = this.getCardShapeKey(card);
    const parsedPreferredVariantId = parseInt(preferredVariantId, 10);
    const hasPreferredVariant = Number.isFinite(parsedPreferredVariantId) && parsedPreferredVariantId > 0;
    const fallbackVariantId = card.dataset.variantId;
    const initialVariantId = hasPreferredVariant ? parsedPreferredVariantId : fallbackVariantId;

    this.state.selectedDiamond = {
      id: card.dataset.productId,
      variantId: initialVariantId,
      handle: card.dataset.productHandle,
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.productPrice) || 0,
      image: card.dataset.productImage,
      spec: card.dataset.specLine || '',
      carat: this.parseCaratValue(card.dataset.specLine || card.dataset.productTitle || ''),
      shape: inferredShape,
      variantTitle: card.dataset.variantTitle || '',
      sku: card.dataset.variantSku || '',
    };
    this.state.selectedDiamondCollectionHandle = this.diamondCollectionHandle || '';
    this.shapeFilterDisabled = false;

    const nextShapeKey = this.getSelectedDiamondShapeKey();
    const shapeChanged = previousShapeKey !== nextShapeKey;
    if (shapeChanged) {
      this.clearSettingSelection();
      this.settingsLoaded = false;
      this.lastAppliedSettingsShapeKey = '';
      this._settingsFiltersMarkup = '';
      this._settingsCountMarkup = '';
    }

    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();
    this.saveState();

    if (hasPreferredVariant && String(parsedPreferredVariantId) !== String(fallbackVariantId)) {
      this.syncDiamondVariantDetails(card.dataset.productHandle, parsedPreferredVariantId);
    }

    // Preload Step 2 collection once a diamond is chosen so Step 1 -> Step 2 feels instant.
    if (this.needsSettingsCollectionRefresh() && !this._settingsPrefetchPromise) {
      this._settingsPrefetchPromise = this.loadSettingsCollection(null, { prefetchOnly: true })
        .catch(() => {})
        .finally(() => {
          this._settingsPrefetchPromise = null;
        });
    }
  }

  async syncDiamondVariantDetails(handle, variantId) {
    const parsedVariantId = parseInt(variantId, 10);
    if (!handle || !Number.isFinite(parsedVariantId) || parsedVariantId <= 0) return;

    try {
      const productData = await this.fetchProductJSON(handle);
      if (!productData || !Array.isArray(productData.variants)) return;
      if (!this.state.selectedDiamond || this.state.selectedDiamond.handle !== handle) return;
      if (parseInt(this.state.selectedDiamond.variantId, 10) !== parsedVariantId) return;

      const variant = productData.variants.find((item) => parseInt(item.id, 10) === parsedVariantId);
      if (!variant) return;

      const nextPrice = parseInt(variant.price, 10);
      if (Number.isFinite(nextPrice)) {
        this.state.selectedDiamond.price = nextPrice;
      }
      if (variant.title) {
        this.state.selectedDiamond.variantTitle = variant.title;
      }
      if (variant.sku) {
        this.state.selectedDiamond.sku = variant.sku;
      }

      const variantImage =
        variant?.featured_image?.src
        || variant?.featured_media?.preview_image?.src
        || '';
      if (variantImage) {
        this.state.selectedDiamond.image = variantImage;
      }

      this.updateSummary();
      this.updateStepImages();
      this.saveState();
    } catch (e) {
      console.warn('Could not sync diamond variant details:', e);
    }
  }

  async selectSetting(card, preferredVariantId = null) {
    const handle = card.dataset.productHandle;
    const parsedPreferredVariantId = parseInt(preferredVariantId, 10);
    const hasPreferredVariant = Number.isFinite(parsedPreferredVariantId) && parsedPreferredVariantId > 0;
    const parsedCardVariantId = parseInt(card.dataset.variantId, 10);
    const hasCardVariant = Number.isFinite(parsedCardVariantId) && parsedCardVariantId > 0;

    this.state.selectedSetting = {
      id: card.dataset.productId,
      variantId: card.dataset.variantId, // FIXED: Store variantId for addToCart fallback
      handle: handle,
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.productPrice) || 0,
      image: card.dataset.productImage,
      variants: null,
      options: null,
    };

    // Reset variant selections
    this.state.selectedVariant = null;
    this.state.selectedMetal = null;
    this.state.selectedStoneShape = null;
    this.state.selectedSettingCaratBucket = null;
    this.state.selectedRingSize = null;

    // Fetch full product JSON for variant data
    try {
      const productData = await this.fetchProductJSON(handle);
      if (productData) {
        this.state.selectedSetting.variants = productData.variants;
        this.state.selectedSetting.options = productData.options;
        this.state.selectedSetting.price = productData.variants[0]?.price || this.state.selectedSetting.price;

        // Set initial variant
        let initialVariant = null;

        if (!initialVariant) {
          initialVariant = this.getAutoMatchedSettingVariant(productData.variants, productData.options);
        }

        if (!initialVariant && hasCardVariant) {
          initialVariant = productData.variants.find((variant) => parseInt(variant.id, 10) === parsedCardVariantId);
        }

        if (!initialVariant && hasPreferredVariant) {
          initialVariant = productData.variants.find((variant) => parseInt(variant.id, 10) === parsedPreferredVariantId);
        }

        if (!initialVariant) {
          initialVariant = productData.variants.find((variant) => variant.available) || productData.variants[0];
        }

        if (initialVariant) {
          this.state.selectedVariant = initialVariant;
          this.state.selectedSetting.price = initialVariant.price;
          this.state.selectedSetting.variantId = initialVariant.id; // FIXED: Keep variantId in sync
          this.extractVariantOptions(initialVariant, productData.options);
        }
      }
    } catch (e) {
      console.warn('Could not fetch setting product JSON:', e);
    }

    this.updateSummary();
    this.updateButtons();
    this.updateStepImages();
    this.saveState();
  }

  extractVariantOptions(variant, options) {
    if (!variant || !options) return;

    // options is array of option names like ["Metal", "Stone Shape", "Ring Size"]
    // variant.options is array of values like ["Gold", "Emerald", "5"]
    options.forEach((optName, i) => {
      const val = variant.options ? variant.options[i] : null;
      if (!val) return;

      const name = typeof optName === 'string' ? optName : optName.name || '';
      if (this.isMetalOptionName(name)) {
        this.state.selectedMetal = val;
      } else if (this.isCaratOptionName(name)) {
        this.state.selectedSettingCaratBucket = val;
      } else if (this.isShapeOptionName(name)) {
        this.state.selectedStoneShape = val;
      } else if (this.isSizeOptionName(name)) {
        this.state.selectedRingSize = val;
      }
    });
  }

  /* ===================== STEP IMAGES ===================== */
  updateStepImages() {
    // Step 1: Diamond image
    const diamondImg = this.querySelector('[data-step-img="1"]');
    const diamondStep = this.querySelector('[data-step="1"]');
    if (diamondImg && diamondStep) {
      if (this.state.selectedDiamond?.image) {
        diamondImg.src = this.state.selectedDiamond.image;
        diamondImg.alt = this.state.selectedDiamond.title || 'Diamond';
        diamondStep.classList.add('has-image');
      } else {
        diamondImg.src = '';
        diamondStep.classList.remove('has-image');
      }
    }

    // Step 2: Setting image
    const settingImg = this.querySelector('[data-step-img="2"]');
    const settingStep = this.querySelector('[data-step="2"]');
    if (settingImg && settingStep) {
      if (this.state.selectedSetting?.image) {
        settingImg.src = this.state.selectedSetting.image;
        settingImg.alt = this.state.selectedSetting.title || 'Setting';
        settingStep.classList.add('has-image');
      } else {
        settingImg.src = '';
        settingStep.classList.remove('has-image');
      }
    }

    // Step 3: Show setting image (or diamond as fallback)
    const reviewImg = this.querySelector('[data-step-img="3"]');
    const reviewStep = this.querySelector('[data-step="3"]');
    if (reviewImg && reviewStep) {
      const img = this.state.selectedSetting?.image || this.state.selectedDiamond?.image;
      if (img) {
        reviewImg.src = img;
        reviewImg.alt = 'Complete Ring';
        reviewStep.classList.add('has-image');
      } else {
        reviewImg.src = '';
        reviewStep.classList.remove('has-image');
      }
    }
  }

  /* ===================== GALLERY (Section Rendering API) ===================== */
  async loadProductGallery(handle, variantId = null) {
    if (!handle) return;

    const previewEl = this.preview;
    if (!previewEl) return;
    this._variantResolutionToken += 1;
    if (this._variantResolutionAbortController) {
      this._variantResolutionAbortController.abort();
      this._variantResolutionAbortController = null;
    }
    this.resetPreviewPosition();
    this.lockPreviewHeightForLoading();
    const hasExistingGallery = !!previewEl.querySelector('.rc2-gallery-wrapper');
    const existingPreviewMarkup = hasExistingGallery ? previewEl.innerHTML : '';

    // Show loading
    if (hasExistingGallery) {
      previewEl.classList.add('has-product');
      previewEl.classList.add('is-overlay-loading');
      this.mountPreviewLoadingOverlay();
    } else {
      previewEl.innerHTML = this.getPreviewSkeletonMarkup();
      previewEl.classList.add('has-product');
    }
    previewEl.classList.add('is-loading');
    previewEl.classList.remove('is-ready');
    previewEl.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}products/${handle}`, window.location.origin);
      url.searchParams.set('section_id', 'rc-product-preview');

      const requestedVariantId = parseInt(variantId, 10);
      const hasRequestedVariantId = Number.isFinite(requestedVariantId) && requestedVariantId > 0;
      if (hasRequestedVariantId) {
        url.searchParams.set('variant', requestedVariantId);
      }

      const response = await fetch(url.toString());
      const html = await response.text();

      // Parse and inject
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('.rc2-gallery-wrapper');
      const variants = doc.querySelector('.rc2-variants-wrapper');
      const specs = doc.querySelector('.rc2-diamond-specs');
      const dataScript = doc.querySelector('[data-rc2-product-json]');
      let parsedPreviewProductData = null;

      if (dataScript) {
        try {
          parsedPreviewProductData = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.warn('Could not parse preview product data:', e);
        }
      }

      if (this.state.currentStep === 2 && variants) {
        const previewOptionNames = Array.isArray(this.state.selectedSetting?.options) && this.state.selectedSetting.options.length
          ? this.state.selectedSetting.options
          : Array.isArray(parsedPreviewProductData?.options)
            ? parsedPreviewProductData.options.map((option) => typeof option === 'string' ? option : option?.name || '')
            : [];

        this.hideInternalCaratOptionFieldsets(variants, previewOptionNames);

        const picker = variants.querySelector('variant-picker');
        if (picker && previewOptionNames.length > 0) {
          this.syncInternalSettingCaratByValue(picker, previewOptionNames);
        }
      }

      let content = '';
      if (wrapper) content += wrapper.outerHTML;
      if (variants) content += variants.outerHTML;
      if (specs) content += specs.outerHTML;

      previewEl.innerHTML = content || '<div class="rc2-preview__empty"><p>Preview not available</p></div>';
      previewEl.classList.remove('is-overlay-loading');
      this.removePreviewLoadingOverlay();

      if (this._previewRevealTimer) {
        window.clearTimeout(this._previewRevealTimer);
      }
      previewEl.classList.add('is-ready');
      this._previewRevealTimer = window.setTimeout(() => {
        previewEl.classList.remove('is-ready');
      }, 320);

      // Parse product data from the preview section
      this.previewProductData = parsedPreviewProductData;

      const renderedVariantId = parseInt(this.previewProductData?.variant_id, 10);
      this.previewProductHandle = this.previewProductData?.handle || handle;
      if (Number.isFinite(renderedVariantId) && renderedVariantId > 0) {
        this.previewVariantId = renderedVariantId;
      } else if (hasRequestedVariantId) {
        this.previewVariantId = requestedVariantId;
      } else {
        this.previewVariantId = null;
      }

      // Re-init theme custom elements in the injected HTML
      this.reinitThemeComponents(previewEl);
      this.setupPreviewMediaBehavior(previewEl);

      // Listen for variant changes from theme's variant picker
      this.listenForVariantChanges(previewEl);
      this.applyInternalSettingOptionsUi(previewEl);

    } catch (error) {
      console.error('Error loading product gallery:', error);
      if (hasExistingGallery && existingPreviewMarkup) {
        previewEl.innerHTML = existingPreviewMarkup;
      } else {
        previewEl.innerHTML = '<div class="rc2-preview__empty"><p>Failed to load preview</p></div>';
      }
    } finally {
      previewEl.classList.remove('is-loading');
      previewEl.classList.remove('is-overlay-loading');
      this.removePreviewLoadingOverlay();
      previewEl.removeAttribute('aria-busy');
      this.releasePreviewHeightLock();
    }
  }

  reinitThemeComponents(container) {
    // Theme's media-gallery and variant-picker are custom elements
    // that initialize on connectedCallback. Re-inserting them into DOM
    // triggers initialization. We need to upgrade them if not already.
    const mediaGallery = container.querySelector('media-gallery');
    if (mediaGallery && !mediaGallery.initialized) {
      // Force re-init by replacing innerHTML (triggers connectedCallback)
      const parent = mediaGallery.parentNode;
      if (parent) {
        const clone = mediaGallery.cloneNode(true);
        parent.replaceChild(clone, mediaGallery);
      }
    }
  }

  listenForVariantChanges(container) {
    if (this.state.currentStep !== 2) return;

    // FIXED: Disconnect previous observer to prevent memory leak
    if (this._variantObserver) {
      this._variantObserver.disconnect();
      this._variantObserver = null;
    }

    // Theme's variant picker uses radio inputs with name="option-N"
    // or dispatches change events. We listen on the container.
    const variantPicker = container.querySelector('variant-picker');

    if (variantPicker) {
      const handleChange = (options = {}) => {
        this.handleVariantChange(variantPicker, options).catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('Could not update ring setting variant:', error);
          }
        });
      };

      // Listen for change events on radios/selects inside variant picker
      variantPicker.addEventListener('change', () => {
        handleChange();
      });

      // Also observe via MutationObserver for themes that update differently
      const observer = new MutationObserver(() => {
        handleChange();
      });
      observer.observe(variantPicker, { attributes: true, subtree: true, attributeFilter: ['checked', 'selected'] });

      // Store observer for cleanup
      this._variantObserver = observer;
      handleChange({ skipPreviewReload: true });
    }
  }

  resolveSelectedVariantFromPicker(picker) {
    if (!this.state.selectedSetting?.variants) return;
    const variants = this.state.selectedSetting.variants;

    const idInputs = picker?.querySelectorAll('input[name="id"]') || [];
    let fallbackVariantById = null;
    for (const input of idInputs) {
      const candidateId = parseInt(input.value, 10);
      if (!Number.isFinite(candidateId) || candidateId <= 0) continue;
      const variantById = variants.find((variant) => parseInt(variant.id, 10) === candidateId);
      if (!variantById) continue;
      if (!fallbackVariantById) fallbackVariantById = variantById;
      if (!input.disabled) return variantById;
    }

    // Read selected option values from the picker's radio inputs
    const selectedOptions = [];
    let fieldsets = picker.querySelectorAll('fieldset');
    if (!fieldsets.length) {
      fieldsets = picker.querySelectorAll('.variant-picker__option');
    }
    fieldsets.forEach((fieldset) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        selectedOptions.push(checkedRadio.value);
        return;
      }
      const select = fieldset.querySelector('select');
      if (select) selectedOptions.push(select.value);
    });

    if (selectedOptions.length === 0) return fallbackVariantById;

    // Find matching variant
    const matchingVariant = variants.find((v) => {
      if (!v.options) return false;
      return v.options.every((opt, i) => {
        return selectedOptions[i] && String(opt).toLowerCase() === String(selectedOptions[i]).toLowerCase();
      });
    });

    return matchingVariant || fallbackVariantById || null;
  }

  schedulePreviewVariantReload(variantId) {
    const targetHandle = this.state.selectedSetting?.handle || '';
    const parsedVariantId = parseInt(variantId, 10);
    if (this.state.currentStep !== 2 || !targetHandle) return;
    if (!Number.isFinite(parsedVariantId) || parsedVariantId <= 0) return;

    const currentVariantId = parseInt(this.previewVariantId, 10);
    if (this.previewProductHandle === targetHandle && currentVariantId === parsedVariantId) {
      return;
    }

    this._pendingPreviewVariantReload = { handle: targetHandle, variantId: parsedVariantId };
    if (this._previewVariantReloadTimer) {
      window.clearTimeout(this._previewVariantReloadTimer);
    }

    this._previewVariantReloadTimer = window.setTimeout(() => {
      this._previewVariantReloadTimer = null;
      const pending = this._pendingPreviewVariantReload;
      this._pendingPreviewVariantReload = null;
      if (!pending) return;
      if (this.state.currentStep !== 2) return;
      const renderedVariantId = parseInt(this.previewVariantId, 10);
      if (this.previewProductHandle === pending.handle && renderedVariantId === pending.variantId) {
        return;
      }
      this.loadProductGallery(pending.handle, pending.variantId);
    }, 140);
  }

  async handleVariantChange(picker, options = {}) {
    if (!this.state.selectedSetting?.variants) return;

    const settingOptionNames = this.state.selectedSetting.options || [];
    this.syncInternalSettingCaratByValue(picker, settingOptionNames);
    this.hideInternalCaratOptionFieldsets(picker, settingOptionNames);

    const resolutionToken = ++this._variantResolutionToken;
    if (this._variantResolutionAbortController) {
      this._variantResolutionAbortController.abort();
    }
    this._variantResolutionAbortController = new AbortController();

    let matchingVariant = this.resolveSelectedVariantFromPicker(picker);
    if (!this.doesVariantMatchPickerSelection(matchingVariant, picker)) {
      matchingVariant = await this.resolveVariantFromOptionValues(picker, settingOptionNames, {
        signal: this._variantResolutionAbortController.signal,
      });
    }

    if (resolutionToken !== this._variantResolutionToken || !matchingVariant) return;
    this._variantResolutionAbortController = null;

    const variants = this.state.selectedSetting.variants;
    if (!variants.some((variant) => String(variant.id) === String(matchingVariant.id))) {
      variants.push(matchingVariant);
    }
    this.syncInternalSettingCaratOption(picker, matchingVariant, settingOptionNames);

    const previousVariantId = parseInt(this.state.selectedVariant?.id || this.state.selectedSetting?.variantId, 10);
    const nextVariantId = parseInt(matchingVariant.id, 10);
    const variantChanged = Number.isFinite(nextVariantId) && previousVariantId !== nextVariantId;

    this.state.selectedVariant = matchingVariant;
    this.state.selectedSetting.price = matchingVariant.price;
    this.state.selectedSetting.variantId = matchingVariant.id;

    const variantImage = matchingVariant?.featured_image?.src
      || matchingVariant?.featured_media?.preview_image?.src
      || '';
    if (variantImage) {
      this.state.selectedSetting.image = variantImage;
    }

    // Extract option names
    const optionsList = this.state.selectedSetting.options || [];
    this.extractVariantOptions(matchingVariant, optionsList);

    this.updateSummary();
    this.updateStepImages();
    this.saveState();

    if (!options.skipPreviewReload && variantChanged) {
      this.schedulePreviewVariantReload(nextVariantId);
    }
  }

  getGridSkeletonMarkup(count = null) {
    let skeletonCount = parseInt(count, 10);
    if (!Number.isFinite(skeletonCount) || skeletonCount <= 0) {
      const mobile = this.isMobileViewport();
      const varName = mobile ? '--rc2-columns-mobile' : '--rc2-columns';
      const columnsRaw = getComputedStyle(this).getPropertyValue(varName);
      const columns = parseInt(columnsRaw, 10);
      const safeColumns = Number.isFinite(columns) && columns > 0 ? columns : 2;
      const rows = mobile ? 3 : 2;
      skeletonCount = safeColumns * rows;
    }

    return Array.from({ length: skeletonCount }).map(() => `
      <article class="rc2-card rc2-card--skeleton" aria-hidden="true">
        <div class="rc2-card__image">
          <div class="rc2-skeleton-shimmer rc2-skeleton-media"></div>
          <span class="rc2-skeleton-shimmer rc2-skeleton-chip"></span>
        </div>
        <div class="rc2-card__body">
          <div class="rc2-skeleton-shimmer rc2-skeleton-line rc2-skeleton-line--title"></div>
          <div class="rc2-skeleton-shimmer rc2-skeleton-line rc2-skeleton-line--meta"></div>
          <div class="rc2-skeleton-shimmer rc2-skeleton-line rc2-skeleton-line--price"></div>
          <div class="rc2-skeleton-shimmer rc2-skeleton-btn"></div>
        </div>
      </article>
    `).join('');
  }

  getPreviewSkeletonMarkup() {
    const isSettingsStep = this.state.currentStep === 2;
    const specRows = isSettingsStep ? 2 : 4;
    const specsMarkup = Array.from({ length: specRows }).map(() => `
      <div class="rc2-skeleton-preview-row">
        <span class="rc2-skeleton-shimmer rc2-skeleton-preview-label"></span>
        <span class="rc2-skeleton-shimmer rc2-skeleton-preview-value"></span>
      </div>
    `).join('');

    const optionsMarkup = isSettingsStep ? `
      <div class="rc2-skeleton-preview-options" aria-hidden="true">
        <div class="rc2-skeleton-preview-swatches">
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-swatch"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-swatch"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-swatch"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-swatch"></span>
        </div>
        <div class="rc2-skeleton-preview-sizes">
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-size"></span>
        </div>
      </div>
    ` : '';

    return `
      <div class="rc2-skeleton-preview" aria-hidden="true">
        <div class="rc2-skeleton-shimmer rc2-skeleton-preview-media"></div>
        <div class="rc2-skeleton-preview-dots" aria-hidden="true">
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-dot"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-dot"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-dot"></span>
          <span class="rc2-skeleton-shimmer rc2-skeleton-preview-dot"></span>
        </div>
        <div class="rc2-skeleton-preview-body">
          <div class="rc2-skeleton-shimmer rc2-skeleton-line rc2-skeleton-line--title"></div>
          <div class="rc2-skeleton-preview-specs">
            ${specsMarkup}
          </div>
          ${optionsMarkup}
          <div class="rc2-skeleton-shimmer rc2-skeleton-line rc2-skeleton-line--price"></div>
        </div>
      </div>
    `;
  }

  clearPreview() {
    if (!this.preview) return;
    this.preview.style.minHeight = '';
    this.preview.style.height = '';
    this.preview.style.removeProperty('--rc2-preview-lock-height');
    this.preview.classList.remove('is-height-locked');
    if (this._previewHeightUnlockTimer) {
      window.clearTimeout(this._previewHeightUnlockTimer);
      this._previewHeightUnlockTimer = null;
    }
    if (this._previewHeightUnlockFrame) {
      window.cancelAnimationFrame(this._previewHeightUnlockFrame);
      this._previewHeightUnlockFrame = null;
    }
    if (this._previewMediaObserver) {
      this._previewMediaObserver.disconnect();
      this._previewMediaObserver = null;
    }
    this.previewProductHandle = '';
    this.previewVariantId = null;
    this._pendingPreviewVariantReload = null;
    let message = 'Select a product to preview';
    if (this.state.currentStep === 1) {
      message = 'Choose a diamond to start';
    } else if (this.state.currentStep === 2) {
      message = 'Choose a setting to preview';
    }

    this.preview.classList.remove('has-product');
    this.preview.innerHTML = `
      <div class="rc2-preview__empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        <p>${this.escapeHtml(message)}</p>
      </div>
    `;
  }

  /* ===================== COLLECTION HYDRATION (Section Rendering API) ===================== */
  buildSectionRenderUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);
    this.applyThemeContext(url);
    url.searchParams.set('section_id', this.renderSectionId);
    return url;
  }

  async fetchRenderedSectionDocument(rawUrl) {
    const renderUrl = this.buildSectionRenderUrl(rawUrl).toString();

    if (!this._sectionRenderCache.has(renderUrl)) {
      const sectionRequest = fetch(renderUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Section render request failed: ${response.status}`);
          }
          return response.text();
        })
        .then((payload) => {
          let sectionHtml = payload;
          try {
            sectionHtml = JSON.parse(payload)?.[this.renderSectionId] || '';
          } catch (_error) {
            // section_id responses are plain HTML; keep payload as-is for backward compatibility.
          }

          if (!sectionHtml || sectionHtml === 'null') {
            throw new Error(`Missing rendered section: ${this.renderSectionId}`);
          }

          return sectionHtml;
        })
        .catch((error) => {
          this._sectionRenderCache.delete(renderUrl);
          throw error;
        });

      this._sectionRenderCache.set(renderUrl, sectionRequest);
    }

    const sectionHtml = await this._sectionRenderCache.get(renderUrl);
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    if (!doc.querySelector('#ProductGridContainer')) {
      this._sectionRenderCache.delete(renderUrl);
      throw new Error(`Source section did not render product container: ${this.renderSectionId}`);
    }
    return doc;
  }

  syncPagination(doc, destinationGrid) {
    if (!destinationGrid) return;
    const sourcePagination = doc.querySelector('[data-pagination]');
    const existingPagination = destinationGrid.parentElement?.querySelector('.rc2-pagination');
    if (existingPagination) existingPagination.remove();
    if (sourcePagination) {
      destinationGrid.insertAdjacentHTML('afterend', sourcePagination.outerHTML);
    }
    this.setupInfinitePaginationObserver();
  }

  syncFilterUi(doc) {
    const sourceFilters = doc.querySelector('#RC2FiltersWrap');
    const destFilters = this.querySelector('#RC2FiltersWrap');
    if (sourceFilters && destFilters) {
      destFilters.innerHTML = sourceFilters.innerHTML;
      this.removeHiddenFilterGroups(destFilters);
    } else if (sourceFilters && this.content) {
      const firstPanel = this.content.querySelector('.rc2-step-panel');
      if (firstPanel) {
        firstPanel.insertAdjacentHTML('beforebegin', sourceFilters.outerHTML);
      } else {
        this.content.insertAdjacentHTML('afterbegin', sourceFilters.outerHTML);
      }
      this.removeHiddenFilterGroups(this.content);
    }

    const sourceCount = doc.querySelector('#ProductCount');
    const destCount = this.querySelector('#ProductCount');
    if (sourceCount && destCount) {
      destCount.innerHTML = sourceCount.innerHTML;
    }

    const sourceGrid = doc.querySelector('#RC2Grid');
    if (sourceGrid?.dataset.collectionHandle) {
      this.activeFilterCollectionHandle = sourceGrid.dataset.collectionHandle;
    }
  }

  getNextPaginationUrl(scope = null) {
    const searchRoot = scope || this;
    if (!searchRoot || typeof searchRoot.querySelector !== 'function') return '';

    const loadMoreBtn = searchRoot.querySelector('[data-pagination] [data-load-more]');
    const nextUrlFromButton = String(loadMoreBtn?.dataset?.nextUrl || '').trim();
    if (nextUrlFromButton) {
      return new URL(nextUrlFromButton, window.location.origin).toString();
    }

    const nextLink = searchRoot.querySelector('.rc2-pagination a.next[href], [data-pagination] a.next[href]');
    const nextHref = nextLink?.getAttribute('href')?.trim();
    if (nextHref) {
      return new URL(nextHref, window.location.origin).toString();
    }

    return '';
  }

  async ensureSettingsShapeMatchesLoaded(grid, shapeKey, initialResult = null) {
    if (!grid || !shapeKey) {
      return initialResult || { filtered: false, visibleCount: 0, totalCount: 0 };
    }

    let result = initialResult || this.applyClientSideSettingsShapeFilter(grid, shapeKey);
    let nextUrl = this.getNextPaginationUrl(grid.parentElement || this);
    const visitedUrls = new Set();
    let safetyCounter = 0;

    while (result.visibleCount === 0 && nextUrl && safetyCounter < 24) {
      if (visitedUrls.has(nextUrl)) break;
      visitedUrls.add(nextUrl);
      safetyCounter += 1;

      const doc = await this.fetchRenderedSectionDocument(nextUrl);
      const sourceGrid = doc.querySelector('#RC2Grid') || doc.querySelector('#RC2SettingsGrid');

      if (sourceGrid) {
        sourceGrid.querySelectorAll('.rc2-card').forEach((card) => {
          grid.appendChild(card);
        });
      }

      this.syncPagination(doc, grid);
      result = this.applyClientSideSettingsShapeFilter(grid, shapeKey);
      nextUrl = this.getNextPaginationUrl(doc) || this.getNextPaginationUrl(grid.parentElement || this);
    }

    return result;
  }

  hydrateManualSettingsProducts(options = {}) {
    if (!this.usesManualSettingsProducts) return;

    const grid = this.querySelector('#RC2SettingsGrid');
    if (!grid) return;

    grid.classList.remove('is-loading');
    grid.removeAttribute('aria-busy');

    if (this._manualSettingsGridMarkup && grid.querySelectorAll('.rc2-card').length === 0) {
      grid.innerHTML = this._manualSettingsGridMarkup;
    }

    grid.dataset.collectionHandle = 'manual-settings';
    grid.dataset.manualSettings = 'true';
    this.clearSettingsFilterUiForManualProducts();
    this.activeFilterCollectionHandle = 'manual-settings';
    this._settingsFiltersMarkup = '';
    this._settingsCountMarkup = '';

    const appliedShapeKey = this.getActiveSettingsShapeKey();
    grid.dataset.shapeMatchKey = appliedShapeKey;

    let shapeFilterResult = this.applyClientSideSettingsShapeFilter(grid, appliedShapeKey);
    if (shapeFilterResult.filtered) {
      this._settingsCountMarkup = this.escapeHtml(
        this.getShapeMatchCountText(shapeFilterResult.visibleCount, shapeFilterResult.totalCount)
      );
    }

    this.settingsLoaded = true;
    this.lastAppliedSettingsShapeKey = appliedShapeKey;

    const visibleCards = Array.from(grid.querySelectorAll('.rc2-card')).filter((card) => !card.hidden);
    if (appliedShapeKey && visibleCards.length === 0) {
      grid.innerHTML = this.getNoShapeMatchMarkup(this.getSelectedDiamondShapeLabel());
      this.clearSettingSelection({ resetGridSelection: false });
      this.updateSummary();
      this.updateButtons();
      this.updateStepImages();
      this.saveState();
      return;
    }

    let selectedCard = null;
    if (this.state.selectedSetting) {
      selectedCard = this.restoreCardSelection('#RC2SettingsGrid', this.state.selectedSetting.handle);
    }

    if (options.focusSelectedCard) {
      const focused = this.scrollToSelectedCard(2, selectedCard);
      if (!focused) {
        window.requestAnimationFrame(() => this.scrollToTop());
      }
    }
  }

  async loadDiamondCollection(urlOverride = null, options = {}) {
    const handle = this.diamondCollectionHandle;
    if (!handle) return;

    const grid = this.querySelector('#RC2Grid');
    if (!grid) return;

    grid.classList.add('is-loading');
    grid.innerHTML = this.getGridSkeletonMarkup();
    grid.setAttribute('aria-busy', 'true');

    try {
      const url = urlOverride || this.getDiamondCollectionUrl(handle);
      const doc = await this.fetchRenderedSectionDocument(url);
      const sourceGrid = doc.querySelector('#RC2Grid');

      if (!sourceGrid || sourceGrid.children.length === 0) {
        grid.innerHTML = '<div class="rc2-empty"><p>No diamonds found in this collection.</p></div>';
        return;
      }

      grid.innerHTML = sourceGrid.innerHTML;
      grid.dataset.collectionHandle = handle;
      this.syncPagination(doc, grid);
      const sourceFilters = doc.querySelector('#RC2FiltersWrap');
      const sourceCount = doc.querySelector('#ProductCount');
      this._diamondFiltersMarkup = sourceFilters ? sourceFilters.innerHTML : '';
      this._diamondCountMarkup = sourceCount ? sourceCount.innerHTML : '';
      this.syncFilterUi(doc);
      this.activeFilterCollectionHandle = handle;
      this.diamondCollectionHydrated = true;

      let selectedCard = null;
      if (this.state.selectedDiamond) {
        selectedCard = this.restoreCardSelection('#RC2Grid', this.state.selectedDiamond.handle);
      } else if (this.state.currentStep === 1) {
        this.clearPreview();
      }

      if (options.focusSelectedCard) {
        const focused = this.scrollToSelectedCard(1, selectedCard);
        if (!focused) {
          window.requestAnimationFrame(() => this.scrollToTop());
        }
      }
    } catch (error) {
      console.error('Error loading diamond collection:', error);
      grid.innerHTML = '<div class="rc2-empty"><p>Failed to load diamonds. Please try refreshing.</p></div>';
    } finally {
      grid.removeAttribute('aria-busy');
      grid.classList.remove('is-loading');
    }
  }

  async loadSettingsCollection(urlOverride = null, options = {}) {
    if (this.usesManualSettingsProducts) {
      this.hydrateManualSettingsProducts(options);
      return;
    }

    const handle = this.settingsCollectionHandle;
    if (!handle) return;

    const grid = this.querySelector('#RC2SettingsGrid');
    if (!grid) return;

    const prefetchOnly = options.prefetchOnly === true;
    const shouldShowSkeleton = !prefetchOnly || this.state.currentStep === 2;
    const root = window.Shopify?.routes?.root || '/';
    const requestUrl = new URL(urlOverride || `${root}collections/${handle}`, window.location.origin);
    requestUrl.searchParams.delete('section_id');
    const unfilteredRequestUrl = new URL(requestUrl.toString());

    const appliedShapeKey = this.shouldApplyShapeMatching()
      ? this.applyShapeFilterParamsToUrl(requestUrl)
      : (() => {
        this.removeShapeFilterParamsFromUrl(requestUrl);
        return '';
      })();

    this.lastSettingsRequestUrl = requestUrl.toString();

    grid.classList.add('is-loading');
    if (shouldShowSkeleton) {
      grid.innerHTML = this.getGridSkeletonMarkup();
    }
    grid.setAttribute('aria-busy', 'true');

    try {
      // Use Section Rendering API so Shopify provides collection context + filters
      let doc = await this.fetchRenderedSectionDocument(requestUrl.toString());

      // Extract grid content from the fetched section
      let sourceGrid = doc.querySelector('#RC2Grid');

      const shouldRetryWithoutServerShapeFilter = appliedShapeKey
        && sourceGrid
        && sourceGrid.querySelectorAll('.rc2-card').length === 0;

      if (shouldRetryWithoutServerShapeFilter) {
        const fallbackUrl = new URL(unfilteredRequestUrl.toString());
        this.removeShapeFilterParamsFromUrl(fallbackUrl);
        doc = await this.fetchRenderedSectionDocument(fallbackUrl.toString());
        sourceGrid = doc.querySelector('#RC2Grid');
      }

      if (sourceGrid) {
        grid.innerHTML = sourceGrid.innerHTML;
      } else {
        // Fallback: try the settings grid container
        const sourceSettingsGrid = doc.querySelector('#RC2SettingsGrid');
        if (sourceSettingsGrid) {
          grid.innerHTML = sourceSettingsGrid.innerHTML;
        } else {
          grid.innerHTML = '<div class="rc2-empty"><p>No ring settings found in this collection.</p></div>';
          return;
        }
      }

      grid.dataset.collectionHandle = handle;
      grid.dataset.shapeMatchKey = appliedShapeKey;
      this.syncPagination(doc, grid);
      const sourceFilters = doc.querySelector('#RC2FiltersWrap');
      const sourceCount = doc.querySelector('#ProductCount');
      this._settingsFiltersMarkup = sourceFilters ? sourceFilters.innerHTML : '';
      this._settingsCountMarkup = sourceCount ? sourceCount.innerHTML : '';

      if (!prefetchOnly) {
        this.syncFilterUi(doc);
        this.activeFilterCollectionHandle = handle;
      }

      let shapeFilterResult = this.applyClientSideSettingsShapeFilter(grid, appliedShapeKey);
      if (appliedShapeKey && shapeFilterResult.visibleCount === 0) {
        shapeFilterResult = await this.ensureSettingsShapeMatchesLoaded(grid, appliedShapeKey, shapeFilterResult);
      }
      if (shapeFilterResult.filtered) {
        const shapeCountText = this.getShapeMatchCountText(shapeFilterResult.visibleCount, shapeFilterResult.totalCount);
        this._settingsCountMarkup = this.escapeHtml(shapeCountText);
        if (!prefetchOnly) {
          this.updateShapeMatchProductCount(shapeFilterResult.visibleCount, shapeFilterResult.totalCount);
        }
      }
      this.settingsLoaded = true;
      this.lastAppliedSettingsShapeKey = appliedShapeKey;

      const visibleCards = Array.from(grid.querySelectorAll('.rc2-card')).filter((card) => !card.hidden);
      if (appliedShapeKey && visibleCards.length === 0) {
        const pagination = grid.parentElement?.querySelector('.rc2-pagination');
        if (pagination) pagination.remove();
        grid.innerHTML = this.getNoShapeMatchMarkup(this.getSelectedDiamondShapeLabel());
        this.clearSettingSelection({ resetGridSelection: false });
        this.updateSummary();
        this.updateButtons();
        this.updateStepImages();
        this.saveState();
      } else {
        // Re-select if we had a previous selection
        let selectedCard = null;
        if (this.state.selectedSetting) {
          selectedCard = this.restoreCardSelection('#RC2SettingsGrid', this.state.selectedSetting.handle);
        }

        if (options.focusSelectedCard) {
          const focused = this.scrollToSelectedCard(2, selectedCard);
          if (!focused) {
            window.requestAnimationFrame(() => this.scrollToTop());
          }
        }
      }

    } catch (error) {
      console.error('Error loading settings collection:', error);
      grid.innerHTML = '<div class="rc2-empty"><p>Failed to load ring settings. Please try refreshing.</p></div>';
      grid.dataset.shapeMatchKey = '';
      this.lastAppliedSettingsShapeKey = '';
    } finally {
      grid.removeAttribute('aria-busy');
      grid.classList.remove('is-loading');
    }
  }

  /* ===================== REVIEW STEP ===================== */
  renderReview() {
    // Panel visibility is handled by CSS (.is-visible class set in goToStep/loadState)
    const panel = this.querySelector('#RC2Panel3');
    if (panel) {
      panel.classList.add('is-visible');
    }

    const container = this.querySelector('#RC2ReviewContent');
    if (!container) return;

    const diamond = this.state.selectedDiamond;
    const setting = this.state.selectedSetting;

    if (!diamond || !setting) {
      container.innerHTML = '<div class="rc2-empty"><p>Please select both a diamond and setting.</p></div>';
      return;
    }

    if (this.isEngravingConfiguredInSection() && !this.engravingProduct && !this._engravingFetchPromise) {
      this.loadEngravingProduct();
    }

    const diamondPrice = diamond.price || 0;
    const settingPrice = setting.price || 0;
    const hasEngravingOption = this.isEngravingConfiguredInSection();
    const engravingProduct = this.engravingProduct;
    let engravingEnabled = !!this.state.engravingEnabled;
    if (!hasEngravingOption) engravingEnabled = false;
    if (engravingProduct && engravingProduct.available === false) engravingEnabled = false;
    const engravingText = this.normalizeEngravingText(this.state.engravingText);
    this.state.engravingText = engravingText;
    if (engravingEnabled && engravingText.trim().length === 0) engravingEnabled = false;
    this.state.engravingEnabled = engravingEnabled;
    const engravingPrice = engravingEnabled && engravingProduct ? this.getEngravingPrice() : 0;
    const totalPrice = diamondPrice + settingPrice + engravingPrice;
    const cleanSpec = this.cleanGid(diamond.spec);
    const engravingFontLabel = this.getEngravingFontLabel(this.state.engravingFont);

    const diamondDetails = this.getDiamondReviewDetails(diamond, cleanSpec);
    const settingDetails = this.getSettingReviewDetails();

    const renderOrderDetails = (items = [], maxItems = 4) => {
      if (!Array.isArray(items) || items.length === 0) return '';
      const visibleItems = items
        .filter((item) => item && item.label && item.value)
        .slice(0, maxItems);
      if (visibleItems.length === 0) return '';
      return `
        <div class="rc2-review__order-details">
          ${visibleItems.map((item) => `
            <span class="rc2-review__order-detail">
              <strong>${this.escapeHtml(item.label)}:</strong> ${this.escapeHtml(item.value)}
            </span>
          `).join('')}
        </div>
      `;
    };

    const renderDetails = (items) => {
      if (!items || items.length === 0) return '';
      return `
        <ul class="rc2-review__details">
          ${items.map((item) => `
            <li class="rc2-review__detail">
              <span class="rc2-review__detail-label">${this.escapeHtml(item.label)}</span>
              <strong class="rc2-review__detail-value">${this.escapeHtml(item.value)}</strong>
            </li>
          `).join('')}
        </ul>
      `;
    };

    const renderEngravingOption = () => {
      if (!hasEngravingOption) return '';

      if (!engravingProduct && this._engravingFetchPromise) {
        return `
          <div class="rc2-review__engraving rc2-review__engraving--loading">
            <div class="rc2-review__engraving-skeleton" aria-hidden="true">
              <div class="rc2-review__engraving-head">
                <span class="rc2-skeleton-shimmer rc2-review__engraving-skel-title"></span>
                <span class="rc2-skeleton-shimmer rc2-review__engraving-skel-price"></span>
              </div>
              <span class="rc2-skeleton-shimmer rc2-review__engraving-skel-copy"></span>
              <span class="rc2-skeleton-shimmer rc2-review__engraving-skel-copy short"></span>
              <div class="rc2-review__engraving-actions">
                <span class="rc2-skeleton-shimmer rc2-review__engraving-skel-btn"></span>
              </div>
            </div>
          </div>
        `;
      }

      if (!engravingProduct) {
        return `
          <div class="rc2-review__engraving">
            <p class="rc2-review__engraving-note">Engraving product is not available right now.</p>
          </div>
        `;
      }

      const unavailable = engravingProduct.available === false;
      const hasConfiguredEngraving = engravingEnabled && engravingText.trim().length > 0;

      return `
        <div class="rc2-review__engraving${hasConfiguredEngraving ? ' is-configured' : ''}">
          <div class="rc2-review__engraving-main">
            <div class="rc2-review__engraving-head">
              <span class="rc2-review__engraving-title">${hasConfiguredEngraving ? 'Engraving Added' : 'Add Engraving'}</span>
              <span class="rc2-review__engraving-price">+ ${this.formatMoney(engravingProduct.price || 0)}</span>
            </div>
            <p class="rc2-review__engraving-description">
              ${hasConfiguredEngraving
                ? 'Your engraving is saved and will be added to this ring.'
                : 'Personalize your ring with custom text and a font preview.'}
            </p>
            ${hasConfiguredEngraving ? `
              <div class="rc2-review__engraving-summary">
                <span class="rc2-review__engraving-chip">${this.escapeHtml(engravingText.trim())}</span>
                <span class="rc2-review__engraving-chip">Font: ${this.escapeHtml(engravingFontLabel)}</span>
              </div>
            ` : ''}
          </div>
          <div class="rc2-review__engraving-actions">
            <button class="rc2-review__engraving-btn rc2-review__engraving-btn--primary" type="button" data-engraving-open ${unavailable ? 'disabled' : ''}>
              ${hasConfiguredEngraving ? 'Edit Engraving' : 'Customize Engraving'}
            </button>
            ${hasConfiguredEngraving ? `
              <button class="rc2-review__engraving-btn" type="button" data-engraving-remove ${unavailable ? 'disabled' : ''}>
                Remove
              </button>
            ` : ''}
          </div>
          ${unavailable ? '<p class="rc2-review__engraving-note">Engraving is currently sold out.</p>' : ''}
        </div>
      `;
    };

    container.innerHTML = `
      <div class="rc2-review">

        <!-- LEFT: Hero cards + back link -->
        <div class="rc2-review__main">

          <div class="rc2-review__hero">

            <div class="rc2-review__card rc2-review__card--diamond">
              <div class="rc2-review__card-image">
                ${diamond.image
                  ? `<img src="${this.escapeHtml(diamond.image)}" alt="${this.escapeHtml(diamond.title)}" loading="lazy">`
                  : '<div class="rc2-review__card-noimg"></div>'}
                <span class="rc2-review__card-badge">Diamond</span>
              </div>
              <div class="rc2-review__card-body">
                <p class="rc2-review__card-name">${this.escapeHtml(diamond.title)}</p>
                ${renderDetails(diamondDetails)}
                ${diamondDetails.length === 0 && cleanSpec ? `<p class="rc2-review__card-spec">${this.escapeHtml(cleanSpec)}</p>` : ''}
                <p class="rc2-review__card-price">${this.formatMoney(diamondPrice)}</p>
                <button class="rc2-review__card-change" type="button" data-change-step="1">Change Diamond</button>
              </div>
            </div>

            <div class="rc2-review__connector" aria-hidden="true">
              <span class="rc2-review__connector-symbol">+</span>
            </div>

            <div class="rc2-review__card rc2-review__card--setting">
              <div class="rc2-review__card-image">
                ${setting.image
                  ? `<img src="${this.escapeHtml(setting.image)}" alt="${this.escapeHtml(setting.title)}" loading="lazy">`
                  : '<div class="rc2-review__card-noimg"></div>'}
                <span class="rc2-review__card-badge">Setting</span>
              </div>
              <div class="rc2-review__card-body">
                <p class="rc2-review__card-name">${this.escapeHtml(setting.title)}</p>
                ${renderDetails(settingDetails)}
                <p class="rc2-review__card-price">${this.formatMoney(settingPrice)}</p>
                <button class="rc2-review__card-change" type="button" data-change-step="2">Change Setting</button>
              </div>
            </div>

          </div>

          <div class="rc2-review__intro rc2-review__intro--mobile">
            <h2>Review Your Ring</h2>
            <p>Confirm your selections before adding to cart.</p>
          </div>

        </div>

        <!-- RIGHT: Sticky summary sidebar -->
        <div class="rc2-review__sidebar">

          <div class="rc2-review__intro rc2-review__intro--sidebar">
            <h2>Review Your Ring</h2>
            <p>Confirm your selections before adding to cart.</p>
          </div>

          <div class="rc2-review__order">
            <div class="rc2-review__order-row">
              <div class="rc2-review__order-info">
                <span class="rc2-review__order-label">Diamond</span>
                <span class="rc2-review__order-name">${this.escapeHtml(diamond.title)}</span>
                ${renderOrderDetails(diamondDetails, 4)}
              </div>
              <span class="rc2-review__order-price">${this.formatMoney(diamondPrice)}</span>
            </div>
            <div class="rc2-review__order-row">
              <div class="rc2-review__order-info">
                <span class="rc2-review__order-label">Setting</span>
                <span class="rc2-review__order-name">${this.escapeHtml(setting.title)}</span>
                ${renderOrderDetails(settingDetails, 4)}
              </div>
              <span class="rc2-review__order-price">${this.formatMoney(settingPrice)}</span>
            </div>
            ${renderEngravingOption()}
            <div class="rc2-review__order-row" data-review-engraving-row ${engravingEnabled ? '' : 'hidden'}>
              <div class="rc2-review__order-info">
                <span class="rc2-review__order-label">Engraving</span>
                <span class="rc2-review__order-name">${this.escapeHtml(engravingProduct?.title || 'Engraving')}</span>
                <div class="rc2-review__order-details">
                  <span class="rc2-review__order-detail">
                    <strong>Text:</strong> <span data-review-engraving-text>${this.escapeHtml(engravingText.trim() || '\u2014')}</span>
                  </span>
                  <span class="rc2-review__order-detail">
                    <strong>Font:</strong> <span data-review-engraving-font>${this.escapeHtml(engravingFontLabel)}</span>
                  </span>
                </div>
              </div>
              <span class="rc2-review__order-price" data-review-engraving-price>${this.formatMoney(engravingPrice)}</span>
            </div>
            <div class="rc2-review__order-row rc2-review__order-row--total">
              <span>Total</span>
              <span data-review-total>${this.formatMoney(totalPrice)}</span>
            </div>
          </div>

          <button class="rc2-atc-btn" type="button" data-atc ${this.isStepThreeActionReady() ? '' : 'disabled'}>
            ${this.escapeHtml(this.getStepActionLabel(3))} &mdash; ${this.formatMoney(totalPrice)}
          </button>

          <div class="rc2-review__actions">
            <button class="rc2-review__edit-btn" type="button" data-change-step="1">Edit Diamond</button>
            <button class="rc2-review__edit-btn" type="button" data-change-step="2">Edit Setting</button>
            <button class="rc2-review__edit-btn rc2-review__edit-btn--reset" type="button" data-reset>Reset</button>
          </div>

          <div class="rc2-trust">
            <div class="rc2-trust__badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              <span>Certified Diamond</span>
            </div>
            <div class="rc2-trust__badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
              <span>Insured Delivery</span>
            </div>
            <div class="rc2-trust__badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <span>Crafted in 25 days</span>
            </div>
          </div>

        </div>

      </div>
    `;

    // Bind change buttons (including back link)
    container.querySelectorAll('[data-change-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.changeStep);
        this.goToStep(step);
      });
    });

    // Bind Add to Cart button in review
    const atcBtn = container.querySelector('[data-atc]');
    if (atcBtn) {
      atcBtn.addEventListener('click', () => this.addToCart());
    }

    this.updateReviewSummaryUi();
  }

  getEngravingFontConfig(fontId = '') {
    const normalizedId = String(fontId || '').trim();
    return this.engravingFonts.find((font) => font.id === normalizedId) || this.engravingFonts[0];
  }

  normalizeEngravingFont(fontId = '') {
    return this.getEngravingFontConfig(fontId).id;
  }

  getEngravingFontLabel(fontId = '') {
    return this.getEngravingFontConfig(fontId).label;
  }

  getEngravingPreviewScale(charCount = 0) {
    const count = parseInt(charCount, 10) || 0;
    if (count >= 20) return 0.68;
    if (count >= 16) return 0.78;
    if (count >= 12) return 0.88;
    return 1;
  }

  setEngravingModalError(message = '') {
    if (!this.engravingModalError) return;
    const nextMessage = String(message || '').trim();
    this.engravingModalError.textContent = nextMessage;
    this.engravingModalError.hidden = nextMessage.length === 0;
  }

  getDesignModeEngravingModalKey() {
    return `rc2-engraving-open:${this.sectionId}`;
  }

  setDesignModeEngravingModalState(isOpen) {
    if (!this.isThemeEditor || !document.body) return;
    const key = this.getDesignModeEngravingModalKey();
    if (isOpen) {
      document.body.setAttribute('data-rc2-engraving-open', key);
    } else if (document.body.getAttribute('data-rc2-engraving-open') === key) {
      document.body.removeAttribute('data-rc2-engraving-open');
    }
  }

  shouldRestoreDesignModeEngravingModal() {
    if (!this.isThemeEditor || !document.body) return false;
    return document.body.getAttribute('data-rc2-engraving-open') === this.getDesignModeEngravingModalKey();
  }

  syncEngravingDraftFromState() {
    this.engravingDraft = {
      text: this.normalizeEngravingText(this.state.engravingText || ''),
      font: this.normalizeEngravingFont(this.state.engravingFont),
    };
  }

  resetEngravingModalScroll() {
    if (this.engravingModalContent) {
      this.engravingModalContent.scrollTop = 0;
    }

    if (this.engravingModalScrollable) {
      this.engravingModalScrollable.scrollTop = 0;
    }
  }

  updateEngravingModalUi() {
    if (!this.engravingModal) return;

    const normalizedText = this.normalizeEngravingText(this.engravingDraft.text);
    const trimmedText = normalizedText.trim();
    const font = this.getEngravingFontConfig(this.engravingDraft.font);

    this.engravingDraft.text = normalizedText;
    this.engravingDraft.font = font.id;

    if (this.engravingModalInput && this.engravingModalInput.value !== normalizedText) {
      this.engravingModalInput.value = normalizedText;
    }

    if (this.engravingModalCount) {
      this.engravingModalCount.textContent = String(normalizedText.length);
    }

    if (this.engravingModalPreviewOverlay) {
      this.engravingModalPreviewOverlay.style.setProperty('--rc2-engraving-scale', String(this.getEngravingPreviewScale(trimmedText.length)));
    }

    if (this.engravingModalPreviewText) {
      this.engravingModalPreviewText.textContent = trimmedText || 'Your text';
      this.engravingModalPreviewText.style.fontFamily = font.fontFamily;
      this.engravingModalPreviewText.classList.toggle('is-placeholder', trimmedText.length === 0);
    }

    if (this.engravingModalPreviewCurveText) {
      this.engravingModalPreviewCurveText.textContent = trimmedText || 'Your text';
      this.engravingModalPreviewCurveText.style.fontFamily = font.fontFamily;
    }

    if (this.engravingModalPreviewCurve) {
      this.engravingModalPreviewCurve.classList.toggle('is-placeholder', trimmedText.length === 0);
    }

    this.engravingModalFontButtons.forEach((button) => {
      const isActive = button.dataset.engravingFontOption === font.id;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.engravingModalApplyBtn) {
      const engravingUnavailable = !this.engravingProduct || this.engravingProduct.available === false;
      this.engravingModalApplyBtn.disabled = engravingUnavailable || trimmedText.length === 0;
    }

    if (trimmedText.length > 0) {
      this.setEngravingModalError('');
    }
  }

  async openEngravingModal() {
    if (!this.isEngravingConfiguredInSection()) return;

    if (!this.engravingProduct && !this._engravingFetchPromise) {
      await this.loadEngravingProduct();
    } else if (this._engravingFetchPromise) {
      await this._engravingFetchPromise;
    }

    if (!this.engravingProduct || this.engravingProduct.available === false) {
      this.showNotification('Engraving is unavailable right now.', 'error');
      return;
    }

    if (!this.engravingModal) return;

    this.syncEngravingDraftFromState();
    this.setEngravingModalError('');
    this.updateEngravingModalUi();
    this.resetEngravingModalScroll();
    this.setDesignModeEngravingModalState(true);
    this.engravingModal.show(this);

    window.requestAnimationFrame(() => {
      this.resetEngravingModalScroll();
    });

    window.setTimeout(() => {
      this.resetEngravingModalScroll();
      if (this.isMobileViewport()) {
        this.engravingModalInput?.blur();
      } else {
        this.engravingModalInput?.focus();
        this.engravingModalInput?.select();
      }
    }, 140);
  }

  applyEngravingDraft() {
    const normalizedText = this.normalizeEngravingText(this.engravingDraft.text);
    const trimmedText = normalizedText.trim();
    const nextFont = this.normalizeEngravingFont(this.engravingDraft.font);

    if (!trimmedText) {
      this.setEngravingModalError('Please enter engraving text before applying.');
      this.engravingModalInput?.focus();
      return;
    }

    this.state.engravingEnabled = true;
    this.state.engravingText = normalizedText;
    this.state.engravingFont = nextFont;
    this.updateSummary();
    this.updateButtons();
    this.saveState();
    this.renderReview();

    if (this.engravingModal?.open && typeof this.engravingModal.hide === 'function') {
      this.engravingModal.hide();
    }
  }

  getDiamondReviewDetails(diamond, cleanSpec = '') {
    const details = [];

    if (diamond?.shape) {
      const label = diamond.shape
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
      details.push({ label: 'Shape', value: label });
    }

    const tokens = (cleanSpec || '')
      .split('•')
      .map((part) => part.trim())
      .filter(Boolean);

    const labels = ['Carat', 'Color', 'Clarity', 'Cut'];
    tokens.forEach((value, index) => {
      if (!value) return;
      details.push({
        label: labels[index] || `Spec ${index + 1}`,
        value
      });
    });

    const title = typeof diamond?.title === 'string' ? diamond.title : '';
    const hasCarat = details.some((item) => item.label.toLowerCase() === 'carat');
    if (!hasCarat && title) {
      const caratMatch = title.match(/(\d+(?:\.\d+)?)\s*carat/i);
      if (caratMatch && caratMatch[1]) {
        details.unshift({ label: 'Carat', value: `${caratMatch[1]}ct` });
      }
    }

    const hasShape = details.some((item) => item.label.toLowerCase() === 'shape');
    if (!hasShape && title) {
      const shapeMatch = title.match(/\b(round|princess|emerald|oval|pear|cushion|radiant|marquise|asscher|heart)\b/i);
      if (shapeMatch && shapeMatch[1]) {
        const shape = shapeMatch[1].charAt(0).toUpperCase() + shapeMatch[1].slice(1).toLowerCase();
        details.push({ label: 'Shape', value: shape });
      }
    }

    if (diamond?.variantTitle && diamond.variantTitle !== 'Default Title') {
      const hasVariant = details.some((item) => item.label.toLowerCase() === 'variant');
      if (!hasVariant) details.push({ label: 'Variant', value: String(diamond.variantTitle) });
    }

    if (diamond?.sku) {
      const hasSku = details.some((item) => item.label.toLowerCase() === 'sku');
      if (!hasSku) details.push({ label: 'SKU', value: String(diamond.sku) });
    }

    return details;
  }

  getSettingReviewDetails() {
    const details = [];
    const setting = this.state.selectedSetting || {};
    const variant = this.state.selectedVariant || {};

    const optionNamesRaw = Array.isArray(setting.options) ? setting.options : [];
    const optionNames = optionNamesRaw.map((option, index) => {
      if (typeof option === 'string' && option.trim() !== '') return option.trim();
      if (option && typeof option.name === 'string' && option.name.trim() !== '') return option.name.trim();
      return `Option ${index + 1}`;
    });

    const selectedValues = Array.isArray(variant.options) ? variant.options : [];
    selectedValues.forEach((value, index) => {
      if (!value || value === 'Default Title') return;
      const label = optionNames[index] || `Option ${index + 1}`;
      if (this.isCaratOptionName(label)) return;
      details.push({ label, value: String(value) });
    });

    const fallbackDetails = [
      { label: 'Metal', value: this.state.selectedMetal },
      { label: 'Stone Shape', value: this.state.selectedStoneShape },
      { label: 'Ring Size', value: this.state.selectedRingSize },
    ];

    fallbackDetails.forEach((item) => {
      if (!item.value) return;
      const exists = details.some((detail) => detail.label.toLowerCase() === item.label.toLowerCase());
      if (!exists) {
        details.push(item);
      }
    });

    if (variant.sku) {
      details.push({ label: 'SKU', value: String(variant.sku) });
    }

    return details;
  }

  isEngravingConfiguredInSection() {
    return this.engravingOptionEnabled && !!this.engravingProductHandle;
  }

  normalizeEngravingText(value = '') {
    return String(value || '')
      .replace(/[\r\n]+/g, ' ')
      .slice(0, this.engravingMaxChars);
  }

  getEngravingPrice() {
    if (!this.state.engravingEnabled) return 0;
    if (!this.engravingProduct || this.engravingProduct.available === false) return 0;
    return this.engravingProduct.price || 0;
  }

  getOrderTotalPrice() {
    return (this.state.selectedDiamond?.price || 0)
      + (this.state.selectedSetting?.price || 0)
      + this.getEngravingPrice();
  }

  hasValidEngravingSelection() {
    if (!this.state.engravingEnabled) return true;
    if (!this.isEngravingConfiguredInSection()) return false;
    if (!this.engravingProduct || !this.engravingProduct.variantId || this.engravingProduct.available === false) return false;
    return this.normalizeEngravingText(this.state.engravingText).trim().length > 0
      && !!this.normalizeEngravingFont(this.state.engravingFont);
  }

  canRestoreStepThreeState() {
    if (!this.state.selectedDiamond || !this.state.selectedSetting) return false;
    if (!this.isSelectedSettingCompatibleWithDiamond()) return false;

    if (!this.state.engravingEnabled) return true;
    if (!this.isEngravingConfiguredInSection()) return false;

    return this.normalizeEngravingText(this.state.engravingText).trim().length > 0
      && !!this.normalizeEngravingFont(this.state.engravingFont);
  }

  isStepThreeActionReady() {
    return !!(
      this.state.selectedDiamond
      && this.state.selectedSetting
      && this.hasValidEngravingSelection()
      && this.isSelectedSettingCompatibleWithDiamond()
    );
  }

  updateReviewSummaryUi() {
    const reviewContainer = this.querySelector('#RC2ReviewContent');
    if (!reviewContainer) return;

    const total = this.getOrderTotalPrice();
    reviewContainer.querySelectorAll('[data-review-total]').forEach((node) => {
      node.textContent = this.formatMoney(total);
    });

    const atcBtn = reviewContainer.querySelector('[data-atc]');
    if (atcBtn) {
      atcBtn.textContent = `${this.getStepActionLabel(3)} — ${this.formatMoney(total)}`;
      atcBtn.disabled = !this.isStepThreeActionReady();
    }

    const engravingRow = reviewContainer.querySelector('[data-review-engraving-row]');
    if (engravingRow) {
      engravingRow.hidden = !(this.state.engravingEnabled && this.engravingProduct);
    }

    const engravingPriceEl = reviewContainer.querySelector('[data-review-engraving-price]');
    if (engravingPriceEl) {
      engravingPriceEl.textContent = this.formatMoney(this.getEngravingPrice());
    }

    const normalizedText = this.normalizeEngravingText(this.state.engravingText);
    const trimmedText = normalizedText.trim();

    const engravingTextEl = reviewContainer.querySelector('[data-review-engraving-text]');
    if (engravingTextEl) {
      engravingTextEl.textContent = trimmedText || '\u2014';
    }

    const engravingFontEl = reviewContainer.querySelector('[data-review-engraving-font]');
    if (engravingFontEl) {
      engravingFontEl.textContent = this.getEngravingFontLabel(this.state.engravingFont);
    }
  }

  async loadEngravingProduct(force = false) {
    if (!this.isEngravingConfiguredInSection()) {
      const hadSavedEngraving =
        this.state.engravingEnabled
        || this.normalizeEngravingText(this.state.engravingText).trim().length > 0;
      this.engravingProduct = null;
      this.state.engravingEnabled = false;
      this.state.engravingText = '';
      this.state.engravingFont = this.normalizeEngravingFont(this.state.engravingFont);
      this.updateSummary();
      this.updateButtons();
      if (hadSavedEngraving) {
        this.saveState();
      }
      return null;
    }

    if (this.engravingProduct && !force) return this.engravingProduct;
    if (this._engravingFetchPromise && !force) return this._engravingFetchPromise;

    this._engravingFetchPromise = this.fetchProductJSON(this.engravingProductHandle)
      .then((productData) => {
        const previousEnabled = this.state.engravingEnabled;
        const previousText = this.normalizeEngravingText(this.state.engravingText);
        const previousFont = this.normalizeEngravingFont(this.state.engravingFont);
        const variants = Array.isArray(productData?.variants) ? productData.variants : [];
        let selectedVariant = null;

        if (this.engravingVariantId) {
          selectedVariant = variants.find((variant) => parseInt(variant.id, 10) === this.engravingVariantId) || null;
        }

        if (!selectedVariant) {
          selectedVariant = variants.find((variant) => variant.available) || variants[0] || null;
        }

        this.engravingProduct = selectedVariant
          ? {
            id: productData.id,
            handle: productData.handle || this.engravingProductHandle,
            title: productData.title || 'Engraving',
            price: selectedVariant.price || 0,
            variantId: parseInt(selectedVariant.id, 10) || null,
            available: selectedVariant.available !== false,
            variantTitle: selectedVariant.title || '',
          }
          : null;

        if (this.engravingProduct?.variantId) {
          this.engravingVariantId = this.engravingProduct.variantId;
        }

        if (this.state.engravingEnabled && (!this.engravingProduct?.variantId || this.engravingProduct.available === false)) {
          this.state.engravingEnabled = false;
        }
        this.state.engravingText = this.normalizeEngravingText(this.state.engravingText);
        this.state.engravingFont = this.normalizeEngravingFont(this.state.engravingFont);
        const stateChanged =
          previousEnabled !== this.state.engravingEnabled
          || previousText !== this.state.engravingText
          || previousFont !== this.state.engravingFont;
        if (stateChanged) {
          this.saveState();
        }
        return this.engravingProduct;
      })
      .catch((error) => {
        console.warn('Could not fetch engraving product JSON:', error);
        const hadSavedEngraving =
          this.state.engravingEnabled
          || this.normalizeEngravingText(this.state.engravingText).trim().length > 0;
        this.engravingProduct = null;
        this.state.engravingEnabled = false;
        this.state.engravingText = '';
        this.state.engravingFont = this.normalizeEngravingFont(this.state.engravingFont);
        if (hadSavedEngraving) {
          this.saveState();
        }
        return null;
      })
      .finally(() => {
        this._engravingFetchPromise = null;
        this.updateSummary();
        this.updateButtons();
        if (this.state.currentStep === 3) {
          this.renderReview();
        }
      });

    return this._engravingFetchPromise;
  }

  /* ===================== ADD TO CART ===================== */
  async addToCart() {
    const diamond = this.state.selectedDiamond;
    const setting = this.state.selectedSetting;
    const engravingSelected = this.state.engravingEnabled;
    const engravingText = this.normalizeEngravingText(this.state.engravingText).trim();
    const engravingFontId = this.normalizeEngravingFont(this.state.engravingFont);
    const engravingFontLabel = this.getEngravingFontLabel(engravingFontId);
    const engravingVariantId = engravingSelected
      ? parseInt(this.engravingProduct?.variantId || this.engravingVariantId, 10)
      : null;

    if (!diamond || !setting) {
      this.showNotification('Please select both a diamond and a setting.', 'error');
      return;
    }

    if (!this.isSelectedSettingCompatibleWithDiamond()) {
      const shapeLabel = this.getSelectedDiamondShapeLabel();
      this.showNotification(`Please select a ${shapeLabel} setting to match your diamond.`, 'error');
      this.updateButtons();
      this.updateReviewSummaryUi();
      return;
    }

    if (engravingSelected && !engravingText) {
      this.showNotification('Please enter engraving text before continuing.', 'error');
      this.updateButtons();
      this.updateReviewSummaryUi();
      return;
    }

    if (engravingSelected && !engravingFontId) {
      this.showNotification('Please choose an engraving font before continuing.', 'error');
      this.updateButtons();
      this.updateReviewSummaryUi();
      return;
    }

    if (engravingSelected && (!engravingVariantId || !this.engravingProduct || this.engravingProduct.available === false)) {
      this.showNotification('Engraving is unavailable right now. Please remove engraving and continue.', 'error');
      this.updateButtons();
      this.updateReviewSummaryUi();
      return;
    }

    // Determine variant IDs
    const diamondVariantId = parseInt(diamond.variantId);
    // FIXED: Fallback uses setting.variantId (not setting.id which is the PRODUCT ID)
    const settingVariantId = this.state.selectedVariant
      ? parseInt(this.state.selectedVariant.id)
      : parseInt(setting.variantId);

    if (!diamondVariantId || !settingVariantId) {
      this.showNotification('Invalid product selection. Please try again.', 'error');
      return;
    }

    // Check variant availability before attempting cart add
    const settingVariant = this.state.selectedVariant ||
      (setting.variants ? setting.variants.find(v => v.id == settingVariantId) : null);
    if (settingVariant && settingVariant.available === false) {
      const variantTitle = (settingVariant.title && settingVariant.title !== 'Default Title') ? settingVariant.title : '';
      const soldOutMsg = variantTitle
        ? `"${setting.title} — ${variantTitle}" is currently sold out. Please select a different option.`
        : `"${setting.title}" is currently sold out.`;
      this.showNotification(soldOutMsg, 'error');
      return;
    }

    // Disable buttons
    const btns = this.querySelectorAll('.rc2-atc-btn, .rc2-summary__btn');
    btns.forEach((button) => {
      button.disabled = true;
      button.textContent = 'Processing...';
    });

    const ringBundleId = [
      diamondVariantId,
      settingVariantId,
      engravingVariantId || 0,
      engravingText || '',
      engravingFontId || ''
    ].join(':');

    const sharedEngravingProperties = engravingSelected
      ? {
        '_Engraving Added': 'Yes',
        '_Engraving Text': engravingText,
        '_Engraving Font': engravingFontLabel,
      }
      : {};

    const desiredDisplayItems = [
      {
        id: diamondVariantId,
        quantity: 1,
        properties: {
          '_Ring Set': 'Diamond',
          '_RC Bundle': ringBundleId,
          '_RC Line Order': '1',
          '_Paired With': setting.title,
          ...sharedEngravingProperties,
        }
      },
      {
        id: settingVariantId,
        quantity: 1,
        properties: {
          '_Ring Set': 'Setting',
          '_RC Bundle': ringBundleId,
          '_RC Line Order': '2',
          '_Paired With': diamond.title,
          ...(this.state.selectedMetal && { '_Metal': this.state.selectedMetal }),
          ...(this.state.selectedStoneShape && { '_Stone Shape': this.state.selectedStoneShape }),
          ...(this.state.selectedSettingCaratBucket && { '_Setting Carat Bucket': this.state.selectedSettingCaratBucket }),
          ...(this.state.selectedRingSize && { '_Ring Size': this.state.selectedRingSize }),
          ...sharedEngravingProperties,
        }
      }
    ];

    if (engravingSelected && engravingVariantId) {
      desiredDisplayItems.push({
        id: engravingVariantId,
        quantity: 1,
        properties: {
          '_Ring Set': 'Engraving',
          '_RC Bundle': ringBundleId,
          '_RC Line Order': '3',
          '_Engraving Text': engravingText,
          'Engraving Text': engravingText,
          '_Engraving Font': engravingFontLabel,
          'Engraving Font': engravingFontLabel,
          '_Applied To': `${diamond.title} + ${setting.title}`,
        }
      });
    }

    // Shopify may render lines in reverse insertion order on some surfaces (for example, checkout/order views).
    // Insert in reverse so the final visible order stays: Diamond -> Setting -> Engraving.
    const items = desiredDisplayItems.slice().reverse();

    try {
      // Build cart sections for drawer update
      const sectionsToBundle = [];
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const drawerId = cartDrawer.closest('[id^="shopify-section"]')?.id?.replace('shopify-section-', '');
        if (drawerId) sectionsToBundle.push(drawerId);
      }

      const data = {
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      };

      const cartAddUrl = window.theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

      const fetchConfig = window.theme?.utils?.fetchConfig?.('javascript') || {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
        }
      };

      const response = await fetch(cartAddUrl, { ...fetchConfig, body: JSON.stringify(data) });
      const result = await response.json();

      if (response.ok) {
        const root = window.Shopify?.routes?.root || '/';

        // Get updated cart for drawer
        const cartUrl = window.theme?.routes?.cart_url || `${root}cart.js`;
        const cartResponse = await fetch(cartUrl, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        const cartJson = await cartResponse.json();

        if (result.sections) {
          cartJson.sections = result.sections;
        }

        // Theme PubSub integration
        if (window.theme?.pubsub?.publish) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'ring-configurator-v3',
            cart: cartJson,
          });
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result },
        }));

        if (this.checkoutMode === 'direct_checkout') {
          window.location.href = `${root}checkout`;
          return;
        }

        // Reset configurator UI + state so the next build starts cleanly.
        this.resetAfterSuccessfulAddToCart();

        // Open cart drawer or redirect
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
        } else {
          window.location.href = `${root}cart`;
        }

      } else {
        const msg = result.description || result.message || 'Failed to add to cart';
        this.showNotification(msg, 'error');
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Failed to add items to cart. Please try again.', 'error');
    } finally {
      const totalPrice = (diamond?.price || 0) + (setting?.price || 0) + (engravingSelected ? (this.engravingProduct?.price || 0) : 0);
      btns.forEach((button) => {
        button.disabled = false;
        if (button.classList.contains('rc2-atc-btn')) {
          button.textContent = `${this.getStepActionLabel(3)} — ${this.formatMoney(totalPrice)}`;
        } else if (button.classList.contains('rc2-summary__btn')) {
          button.textContent = this.getStepActionLabel(this.state.currentStep);
        }
      });
    }
  }

  /* ===================== LOAD MORE ===================== */
  async handleLoadMore(btn) {
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    const defaultText = btn.dataset.defaultText || 'Load More';
    const loadingText = btn.dataset.loadingText || 'Loading...';
    const btnText = btn.querySelector('[data-text]');

    if (btnText) {
      btnText.textContent = loadingText;
    } else {
      btn.textContent = loadingText;
    }
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const doc = await this.fetchRenderedSectionDocument(nextUrl);
      const newGrid = doc.querySelector('#RC2Grid') || doc.querySelector('#RC2SettingsGrid');
      const newPagination = doc.querySelector('[data-pagination]');

      if (newGrid) {
        const inStep2Panel = !!btn.closest('#RC2Panel2');
        const gridSelector = inStep2Panel ? '#RC2SettingsGrid' : '#RC2Grid';
        const grid = this.querySelector(gridSelector);
        if (grid) {
          // Append new cards
          const cards = newGrid.querySelectorAll('.rc2-card');
          cards.forEach(card => grid.appendChild(card));

          if (inStep2Panel) {
            const shapeKey = this.getActiveSettingsShapeKey();
            grid.dataset.shapeMatchKey = shapeKey;
            const shapeFilterResult = this.applyClientSideSettingsShapeFilter(grid, shapeKey);
            if (shapeFilterResult.filtered) {
              this.updateShapeMatchProductCount(shapeFilterResult.visibleCount, shapeFilterResult.totalCount);
            }

            const visibleCards = Array.from(grid.querySelectorAll('.rc2-card')).filter((card) => !card.hidden);
            if (shapeKey && visibleCards.length === 0) {
              const pagination = grid.parentElement?.querySelector('.rc2-pagination');
              if (pagination) pagination.remove();
              grid.innerHTML = this.getNoShapeMatchMarkup(this.getSelectedDiamondShapeLabel());
              this.clearSettingSelection({ resetGridSelection: false });
              this.updateSummary();
              this.updateButtons();
              this.updateStepImages();
              this.saveState();
            }
          }
        }
      }

      // Update or remove pagination
      const paginationContainer = btn.closest('.rc2-pagination');
      if (newPagination) {
        const newBtn = newPagination.querySelector('[data-load-more]');
        if (newBtn) {
          btn.dataset.nextUrl = newBtn.dataset.nextUrl;
          if (newBtn.dataset.mode) btn.dataset.mode = newBtn.dataset.mode;
          if (newBtn.dataset.defaultText) btn.dataset.defaultText = newBtn.dataset.defaultText;
          if (newBtn.dataset.loadingText) btn.dataset.loadingText = newBtn.dataset.loadingText;
          if (btnText) {
            btnText.textContent = btn.dataset.defaultText || defaultText;
          } else {
            btn.textContent = btn.dataset.defaultText || defaultText;
          }
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          this.setupInfinitePaginationObserver();
        } else {
          paginationContainer?.remove();
        }
      } else {
        paginationContainer?.remove();
      }

    } catch (error) {
      console.error('Error loading more products:', error);
      if (btnText) {
        btnText.textContent = defaultText;
      } else {
        btn.textContent = defaultText;
      }
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }

  async handlePaginationLink(link) {
    const href = link.getAttribute('href');
    if (!href || link.dataset.rc2Loading === 'true') return;

    link.dataset.rc2Loading = 'true';
    link.classList.add('pointer-events-none');

    try {
      const doc = await this.fetchRenderedSectionDocument(href);
      const inStep2Panel = !!link.closest('#RC2Panel2');
      const gridSelector = inStep2Panel ? '#RC2SettingsGrid' : '#RC2Grid';
      const destinationGrid = this.querySelector(gridSelector);
      const sourceGrid = doc.querySelector('#RC2Grid') || doc.querySelector('#RC2SettingsGrid');

      if (sourceGrid && destinationGrid) {
        destinationGrid.innerHTML = sourceGrid.innerHTML;
        if (sourceGrid.dataset.collectionHandle) {
          destinationGrid.dataset.collectionHandle = sourceGrid.dataset.collectionHandle;
          this.activeFilterCollectionHandle = sourceGrid.dataset.collectionHandle;
        }

        this.syncPagination(doc, destinationGrid);
        this.syncFilterUi(doc);

        let hasShapeMatchCards = true;
        if (inStep2Panel) {
          const shapeKey = this.getActiveSettingsShapeKey();
          destinationGrid.dataset.shapeMatchKey = shapeKey;
          const shapeFilterResult = this.applyClientSideSettingsShapeFilter(destinationGrid, shapeKey);
          if (shapeFilterResult.filtered) {
            this.updateShapeMatchProductCount(shapeFilterResult.visibleCount, shapeFilterResult.totalCount);
          }

          const visibleCards = Array.from(destinationGrid.querySelectorAll('.rc2-card')).filter((card) => !card.hidden);
          hasShapeMatchCards = !shapeKey || visibleCards.length > 0;
          if (!hasShapeMatchCards) {
            const pagination = destinationGrid.parentElement?.querySelector('.rc2-pagination');
            if (pagination) pagination.remove();
            destinationGrid.innerHTML = this.getNoShapeMatchMarkup(this.getSelectedDiamondShapeLabel());
            this.clearSettingSelection({ resetGridSelection: false });
            this.updateSummary();
            this.updateButtons();
            this.updateStepImages();
            this.saveState();
          }
        }

        if (inStep2Panel && this.state.selectedSetting && hasShapeMatchCards) {
          this.restoreCardSelection('#RC2SettingsGrid', this.state.selectedSetting.handle);
        } else if (!inStep2Panel && this.state.selectedDiamond) {
          this.restoreCardSelection('#RC2Grid', this.state.selectedDiamond.handle);
        }
      }

      if (this.isCollectionPage) {
        history.replaceState({}, '', new URL(href, window.location.origin).toString());
      }
    } catch (error) {
      console.error('Error loading paginated products:', error);
    } finally {
      link.classList.remove('pointer-events-none');
      delete link.dataset.rc2Loading;
    }
  }

  setupInfinitePaginationObserver() {
    if (!this._infiniteLoadObserver) {
      this._infiniteLoadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const button = entry.target;
          if (!(button instanceof HTMLElement)) return;
          if (button.getAttribute('data-mode') !== 'infinite') return;
          if (button.disabled || button.hasAttribute('aria-busy')) return;
          this.handleLoadMore(button);
        });
      }, {
        root: null,
        rootMargin: '200px 0px 200px 0px',
        threshold: 0.01,
      });
    }

    const infiniteButtons = this.querySelectorAll('[data-load-more][data-mode="infinite"]');
    infiniteButtons.forEach((button) => {
      this._infiniteLoadObserver.observe(button);
    });
  }

  /* ===================== SUMMARY UPDATES ===================== */
  updateSummary() {
    const diamond = this.state.selectedDiamond;
    const setting = this.state.selectedSetting;
    const normalizedEngravingText = this.normalizeEngravingText(this.state.engravingText);
    const engravingFontLabel = this.getEngravingFontLabel(this.state.engravingFont);
    this.state.engravingText = normalizedEngravingText;
    this.state.engravingFont = this.normalizeEngravingFont(this.state.engravingFont);
    const engravingText = normalizedEngravingText.trim();
    const showEngraving = this.state.engravingEnabled && !!this.engravingProduct;

    if (this.sumDiamond) {
      if (diamond) {
        const cleanSpec = this.cleanGid(diamond.spec);
        this.sumDiamond.textContent = cleanSpec || diamond.title;
      } else {
        this.sumDiamond.textContent = '\u2014';
      }
    }

    if (this.sumSetting) {
      this.sumSetting.textContent = setting ? setting.title : '\u2014';
    }

    if (this.sumMetal) {
      this.sumMetal.textContent = this.state.selectedMetal || '\u2014';
    }

    if (this.sumSize) {
      this.sumSize.textContent = this.state.selectedRingSize || '\u2014';
    }

    if (this.sumEngraving && this.sumEngravingRow) {
      this.sumEngravingRow.hidden = !showEngraving;
      this.sumEngraving.textContent = showEngraving
        ? `${this.engravingProduct.title}${engravingText ? ` (${engravingText}${engravingFontLabel ? `, ${engravingFontLabel}` : ''})` : ''}`
        : '\u2014';
    }

    if (this.sumTotal) {
      this.sumTotal.textContent = this.formatMoney(this.getOrderTotalPrice());
    }

    this.updateReviewSummaryUi();
    this.updateMobileStrip();
  }

  updateMobileStrip() {
    if (!this.mobileStrip) return;

    const diamond = this.state.selectedDiamond;
    const setting = this.state.selectedSetting;

    if (this.mobileDiamondCard) {
      this.mobileDiamondCard.classList.toggle('is-empty', !diamond);
    }
    if (this.mobileDiamondImg) {
      if (diamond?.image) {
        this.mobileDiamondImg.src = diamond.image;
        this.mobileDiamondImg.alt = diamond.title || 'Selected diamond';
        this.mobileDiamondImg.hidden = false;
      } else {
        this.mobileDiamondImg.src = '';
        this.mobileDiamondImg.alt = '';
        this.mobileDiamondImg.hidden = true;
      }
    }
    if (this.mobileDiamondLabel) {
      const diamondLabel = diamond ? (this.cleanGid(diamond.spec) || diamond.title || 'Selected') : 'Not selected';
      this.mobileDiamondLabel.textContent = diamondLabel;
    }

    if (this.mobileSettingCard) {
      this.mobileSettingCard.classList.toggle('is-empty', !setting);
    }
    if (this.mobileSettingImg) {
      if (setting?.image) {
        this.mobileSettingImg.src = setting.image;
        this.mobileSettingImg.alt = setting.title || 'Selected setting';
        this.mobileSettingImg.hidden = false;
      } else {
        this.mobileSettingImg.src = '';
        this.mobileSettingImg.alt = '';
        this.mobileSettingImg.hidden = true;
      }
    }
    if (this.mobileSettingLabel) {
      this.mobileSettingLabel.textContent = setting ? setting.title : 'Not selected';
    }

    if (this.mobileDiamondCard) {
      this.mobileDiamondCard.classList.toggle('is-active', this.state.currentStep === 1);
    }
    if (this.mobileSettingCard) {
      this.mobileSettingCard.classList.toggle('is-active', this.state.currentStep === 2);
    }

    if (this.mobileSettingImg && this.state.selectedVariant) {
      const variant = this.state.selectedVariant;
      const variantImage =
        variant?.featured_image?.src
        || variant?.featured_media?.preview_image?.src
        || '';
      if (variantImage) {
        this.mobileSettingImg.src = variantImage;
        this.mobileSettingImg.alt = variant.title || 'Selected setting';
        this.mobileSettingImg.hidden = false;
      }
    }
  }

  /* ===================== UTILITY ===================== */
  async fetchProductJSON(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;

    if (!this._productJsonCache.has(normalizedHandle)) {
      const root = window.Shopify?.routes?.root || '/';
      const productRequest = fetch(`${root}products/${normalizedHandle}.js`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product JSON request failed: ${response.status}`);
          }
          return response.json();
        })
        .catch((error) => {
          this._productJsonCache.delete(normalizedHandle);
          throw error;
        });

      this._productJsonCache.set(normalizedHandle, productRequest);
    }

    return this._productJsonCache.get(normalizedHandle);
  }

  formatMoney(cents) {
    const normalizedCents = parseInt(cents, 10) || 0;
    const themeSettings = window.theme?.settings || {};
    const currencyCodeEnabled = !!themeSettings.currencyCodeEnabled;
    const activeCurrency =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.currentCurrency ||
      null;

    // Prefer active Shopify currency so values follow country selector in header.
    if (activeCurrency && typeof Intl !== 'undefined' && typeof Intl.NumberFormat === 'function') {
      try {
        const locale = document.documentElement.lang || navigator.language || 'en';
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: activeCurrency,
          currencyDisplay: currencyCodeEnabled ? 'code' : 'symbol',
        }).format(normalizedCents / 100);
      } catch (error) {
        // Fall through to theme formatter.
      }
    }

    const format = currencyCodeEnabled
      ? (themeSettings.moneyWithCurrencyFormat || themeSettings.moneyFormat)
      : (themeSettings.moneyFormat || themeSettings.moneyWithCurrencyFormat);

    if (window.theme?.Currency?.formatMoney && format) {
      return window.theme.Currency.formatMoney(normalizedCents, format);
    }

    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && format) {
      return Shopify.formatMoney(normalizedCents, format);
    }

    const amount = (normalizedCents / 100).toFixed(2);
    const amountFormatted = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return currencyCodeEnabled && activeCurrency
      ? `${activeCurrency} ${amountFormatted}`
      : amountFormatted;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Strip Shopify GID references from text.
   * Handles patterns like "gid://shopify/Metaobject/164992123138"
   */
  cleanGid(text) {
    if (!text) return '';
    // Remove GID patterns
    let cleaned = text.replace(/gid:\/\/shopify\/\w+\/\d+/g, '');
    // Clean up leftover separators (multiple dots/bullets)
    cleaned = cleaned.replace(/\s*[•·]\s*[•·]\s*/g, ' • ');
    cleaned = cleaned.replace(/^\s*[•·]\s*/, '');
    cleaned = cleaned.replace(/\s*[•·]\s*$/, '');
    cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
    return cleaned;
  }

  scrollToTop() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stickyHeader = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')
    ) || 0;
    const targetTop = window.scrollY + this.getBoundingClientRect().top - stickyHeader - 12;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  }

  /* ===================== STATE PERSISTENCE ===================== */
  saveState() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (e) { /* ignore */ }
  }

  loadState() {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (!saved) return;

      const parsed = JSON.parse(saved);
      if (parsed && parsed.currentStep) {
        const parsedStep = parseInt(parsed.currentStep, 10);
        this.state = {
          currentStep: Number.isFinite(parsedStep) && parsedStep >= 1 && parsedStep <= 3 ? parsedStep : 1,
          selectedDiamond: parsed.selectedDiamond || null,
          selectedDiamondCollectionHandle: parsed.selectedDiamondCollectionHandle || '',
          selectedSetting: parsed.selectedSetting || null,
          selectedVariant: parsed.selectedVariant || null,
          selectedMetal: parsed.selectedMetal || null,
          selectedStoneShape: parsed.selectedStoneShape || null,
          selectedSettingCaratBucket: parsed.selectedSettingCaratBucket || null,
          selectedRingSize: parsed.selectedRingSize || null,
          engravingEnabled: !!parsed.engravingEnabled,
          engravingText: this.normalizeEngravingText(parsed.engravingText || ''),
          engravingFont: this.normalizeEngravingFont(parsed.engravingFont || ''),
        };
        this.shapeFilterDisabled = false;
        this.lastAppliedSettingsShapeKey = '';

        if (this.state.selectedDiamondCollectionHandle) {
          this.setActiveDiamondCollection(this.state.selectedDiamondCollectionHandle);
        } else {
          this.updateDiamondShapeButtons(this.diamondCollectionHandle);
        }

        if (this.state.selectedDiamond) {
          const savedShape = this.normalizeShapeKey(
            this.state.selectedDiamond.shape || this.getShapeKeyFromText(this.state.selectedDiamond.title || '')
          );
          this.state.selectedDiamond.shape = savedShape;
        }

        if (!this.isEngravingConfiguredInSection()) {
          this.state.engravingEnabled = false;
          this.state.engravingText = '';
          this.state.engravingFont = this.normalizeEngravingFont('');
        }

        if (this.state.engravingEnabled && this.state.engravingText.trim().length === 0) {
          this.state.engravingEnabled = false;
        }

        if (this.state.selectedSetting && !this.isSelectedSettingCompatibleWithDiamond()) {
          this.clearSettingSelection({ resetGridSelection: false });
        }

        if (this.state.currentStep === 3 && !this.canRestoreStepThreeState()) {
          this.state.currentStep = this.state.selectedDiamond ? 2 : 1;
        }

        // Restore UI
        this.updateSummary();
        this.updateButtons();
        this.updateStepImages();

        // Restore step indicators
        this.stepsEl.forEach(s => {
          const sn = parseInt(s.dataset.step);
          s.classList.toggle('is-active', sn === this.state.currentStep);
          s.classList.toggle('is-completed', sn < this.state.currentStep);
          s.classList.remove('is-activating');
        });

        this.lines.forEach(line => {
          const lineKey = line.dataset.line;
          if (lineKey === '1-2') {
            line.classList.toggle('is-completed', this.state.currentStep >= 2);
          } else if (lineKey === '2-3') {
            line.classList.toggle('is-completed', this.state.currentStep >= 3);
          }
        });

        // Update data attribute for CSS-driven layout changes
        this.dataset.currentStep = this.state.currentStep;

        // Show correct panel — clear inline styles so CSS classes control visibility
        this.panels.forEach(p => {
          const pn = parseInt(p.dataset.panel);
          p.classList.toggle('is-visible', pn === this.state.currentStep);
          p.classList.remove('is-entering', 'is-exiting');
          p.style.display = '';
          p.style.visibility = '';
          p.style.opacity = '';
          p.style.height = '';
          p.style.overflow = '';
          p.style.transform = '';
          p.style.clipPath = '';
        });
        if (this.content) {
          this.content.classList.remove('is-panel-switching');
          this.content.style.removeProperty('--rc2-panel-switch-height');
        }

        // Restore step-specific state
        if (this.state.currentStep === 3) {
          this.renderReview();
        }

        // Re-highlight selected diamond card if on step 1
        if (this.state.currentStep === 1 && this.state.selectedDiamond) {
          setTimeout(() => {
            this.restoreCardSelection('#RC2Grid', this.state.selectedDiamond.handle);
            if (!this.isMobileViewport()) {
              this.loadProductGallery(this.state.selectedDiamond.handle);
            }
          }, 300);
        }

        // Hide filters on step 3
        if (this.state.currentStep === 3) {
          const filtersWrap = this.querySelector('#RC2FiltersWrap');
          if (filtersWrap) filtersWrap.style.display = 'none';
        }
      }
    } catch (e) {
      console.warn('Error loading saved state:', e);
    }
  }

  /* ===================== TOAST NOTIFICATION ===================== */
  showNotification(message, type = 'info') {
    // Remove any existing toast
    const existing = document.querySelector('.rc2-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `rc2-toast rc2-toast--${type}`;

    const icon = type === 'error'
      ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`
      : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`;

    toast.innerHTML = `
      <span class="rc2-toast__icon">${icon}</span>
      <span class="rc2-toast__msg">${this.escapeHtml(message)}</span>
      <button class="rc2-toast__close" type="button" aria-label="Dismiss">&times;</button>
    `;

    document.body.appendChild(toast);

    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      toast.classList.add('rc2-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 5000);

    // Manual dismiss
    toast.querySelector('.rc2-toast__close').addEventListener('click', () => {
      clearTimeout(timer);
      toast.classList.add('rc2-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    });
  }

  clearState(targetStep = null) {
    let initialStep = parseInt(targetStep, 10);
    if (!Number.isFinite(initialStep) || initialStep < 1 || initialStep > 3) {
      initialStep = parseInt(this.dataset.initialStep, 10) || 1;
    }
    this.state = {
      currentStep: initialStep,
      selectedDiamond: null,
      selectedDiamondCollectionHandle: this.diamondCollectionHandle || '',
      selectedSetting: null,
      selectedVariant: null,
      selectedMetal: null,
      selectedStoneShape: null,
      selectedSettingCaratBucket: null,
      selectedRingSize: null,
      engravingEnabled: false,
      engravingText: '',
      engravingFont: this.normalizeEngravingFont(''),
    };
    this.shapeFilterDisabled = false;
    this.lastAppliedSettingsShapeKey = '';
    this.lastSettingsRequestUrl = '';
    this.settingsLoaded = false;
    this._settingsFiltersMarkup = '';
    this._settingsCountMarkup = '';
    sessionStorage.removeItem(this.storageKey);

    // Reset step images
    this.querySelectorAll('[data-step-img]').forEach(img => {
      img.src = '';
    });
    this.querySelectorAll('.rc2-step.has-image').forEach(step => {
      step.classList.remove('has-image');
    });
  }
}

// Register custom element
if (!customElements.get('ring-configurator-v3')) {
  customElements.define('ring-configurator-v3', RingConfiguratorV3);
}
